import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import API from '../api';
import ChatWindow from './ChatWindow';
import { unregisterPushSubscription } from '../push';
import { UserMaterials } from './Materials';

const localizer = momentLocalizer(moment);

// Compress an image file to a JPEG data URL under a max dimension.
// Handles large camera photos and unsupported formats (HEIC etc. fail gracefully).
async function compressImage(file, maxDim = 1920, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
        else { width = Math.round(width * maxDim / height); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image decode failed')); };
    img.src = url;
  });
}

/* ── helpers ── */
// Convert IST time to user's local timezone for display
function toLocalTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return timeStr || '';
  try {
    const istDate = new Date(`${dateStr}T${timeStr}+05:30`);
    const h = istDate.getHours();
    const m = istDate.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2,'0')} ${period}`;
  } catch { return timeStr; }
}
function toLocalDatetime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr + (isoStr.endsWith('Z') ? '' : '+05:30'));
    const h = d.getHours(), m = d.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${d.toLocaleDateString()} ${hour}:${String(m).padStart(2,'0')} ${period}`;
  } catch { return isoStr; }
}
const fmtTime = (t, dateStr) => {
  if (!t) return '—';
  // Always treat stored time as IST; use today's date if none provided
  const ref = dateStr || new Date().toISOString().split('T')[0];
  return toLocalTime(ref, t);
};

// Convert local device time (HH:MM) to IST (HH:MM) for storage.
// For Indian tutors this is a no-op; for Qatar/other zones it adjusts correctly.
function localTimeToIST(dateStr, timeStr) {
  if (!dateStr || !timeStr) return timeStr || '';
  try {
    const [h, m] = timeStr.split(':').map(Number);
    const localDate = new Date(`${dateStr}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
    const utcMs = localDate.getTime() + localDate.getTimezoneOffset() * 60000;
    const istMs = utcMs + 5.5 * 3600 * 1000;
    const ist = new Date(istMs);
    return `${String(ist.getHours()).padStart(2,'0')}:${String(ist.getMinutes()).padStart(2,'0')}`;
  } catch { return timeStr; }
}
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, mo, day] = d.split('-');
  return `${day}-${mo}-${y}`;
};

// ── 12-hour TimePicker ────────────────────────────────────────────────────────
function TimePicker12h({ value, onChange, className }) {
  const toDisplay = (v) => {
    if (!v) return { h: '12', m: '00', period: 'AM' };
    const [hh, mm] = v.split(':');
    let h = parseInt(hh);
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return { h: String(h), m: mm || '00', period };
  };
  const { h, m, period } = toDisplay(value);
  const to24 = (hStr, mStr, p) => {
    let hr = parseInt(hStr);
    if (p === 'AM' && hr === 12) hr = 0;
    if (p === 'PM' && hr !== 12) hr += 12;
    return `${String(hr).padStart(2,'0')}:${String(mStr).padStart(2,'0')}`;
  };
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
      <select className={className} value={h} style={{ width:64 }}
        onChange={e => onChange(to24(e.target.value, m, period))}>
        {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{String(i+1).padStart(2,'0')}</option>)}
      </select>
      <span>:</span>
      <select className={className} value={m} style={{ width:64 }}
        onChange={e => onChange(to24(h, e.target.value, period))}>
        {['00','05','10','15','20','25','30','35','40','45','50','55'].map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      <select className={className} value={period} style={{ width:64 }}
        onChange={e => onChange(to24(h, m, e.target.value))}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

/* ── inline style helpers ── */
const card = (extra = {}) => ({
  background: '#fff',
  borderRadius: 20,
  border: '1px solid #e8edf8',
  boxShadow: '0 4px 24px rgba(37,84,184,0.07)',
  padding: 28,
  transition: 'box-shadow 0.25s, transform 0.25s',
  ...extra,
});

const pill = (active, color = '#2554b8') => ({
  padding: '8px 22px',
  borderRadius: 50,
  border: `2px solid ${active ? color : '#e2e8f6'}`,
  background: active ? color : '#fff',
  color: active ? '#fff' : '#64748b',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  outline: 'none',
  boxShadow: active ? `0 4px 14px ${color}44` : 'none',
});

function LiveClock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      let h = now.getHours();
      const m = now.getMinutes();
      const s = now.getSeconds();
      const period = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      setTime(`${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} ${period}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return <span style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:600, fontFamily:'monospace', letterSpacing:0.5 }}>{time}</span>;
}

/* ═══════════════════════════════════════════════════
   SIDEBAR
═══════════════════════════════════════════════════ */
const NAV = [
  { id: 'dashboard',  icon: '⊞',  label: 'Dashboard'       },
  { id: 'profile',    icon: '◎',  label: 'My Profile'       },
  { id: 'enrollment', icon: '❰❱', label: 'Enrollment List'  },
  { id: 'attendance', icon: '✓',  label: 'Attendance'       },
  { id: 'schedule',   icon: '▦',  label: 'Schedules'        },
  { id: 'tests',      icon: '✎',  label: 'Tests & Grades'   },
  { id: 'leave',      icon: '◷',  label: 'Leave Request'    },
  { id: 'payments',   icon: '💰',  label: 'Payment Details'  },
  { id: 'homework',   icon: '📋',  label: 'Homework'         },
  { id: 'help',       icon: '?',  label: 'Help & Support'   },
  { id: 'chat',       icon: '💬',  label: 'Chat'             },
  { id: 'classreq',   icon: '📋',  label: 'Class Requests'   },
  { id: 'feedback',   icon: '⭐',  label: 'Student Feedback' },
  { id: 'queries',    icon: '❓',  label: 'My Queries'       },
  { id: 'materials',  icon: '📚',  label: 'Study Materials'  },
  { id: 'vr',         icon: '🕶️',  label: 'VR Learning'      },
  { id: 'stufeedback',icon: '📝',  label: 'Monthly Feedback' },
];

function useUnreadChats() {
  const [unreadRooms, setUnreadRooms] = useState({});
  const check = useCallback(() => {
    API.get('/chat/rooms').then(r => {
      const map = {};
      (r.data || []).forEach(room => {
        const seen = parseInt(localStorage.getItem(`chat_seen_${room.room_id}`) || '0');
        map[room.room_id] = (room.last_message_id || 0) > seen;
      });
      setUnreadRooms(map);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [check]);
  return [unreadRooms, check];
}

function Sidebar({ active, setActive, unreadCount, sidebarOpen, setSidebarOpen }) {
  const name = localStorage.getItem('name') || 'Companion';
  const [showSignout, setShowSignout] = useState(false);
  const [hovered, setHovered] = useState(null);
  const logout = async () => { await unregisterPushSubscription(); try { await API.post('/auth/logout'); } catch(_){} localStorage.clear(); sessionStorage.clear(); window.location.href = '/login'; };

  return (
    <>
      <style>{`
        @keyframes sidebarSlide { from { opacity:0; transform:translateX(-20px) } to { opacity:1; transform:translateX(0) } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:none } }
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes bounceIn { 0%{transform:scale(.85);opacity:0} 60%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes shimmer { from{background-position:-400px 0} to{background-position:400px 0} }
        @keyframes gradientShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        .t-nav-item { transition: all 0.22s cubic-bezier(.4,0,.2,1); position:relative; overflow:hidden; }
        .t-nav-item::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:#fff; border-radius:0 3px 3px 0; transform:scaleY(0); transition:transform .2s ease; }
        .t-nav-item.t-active::before { transform:scaleY(1); }
        .t-nav-item:hover { background:rgba(255,255,255,0.12) !important; padding-left:26px !important; }
        .t-nav-item.t-active { background:rgba(255,255,255,0.18) !important; padding-left:26px !important; }
        .t-card-hover:hover { box-shadow: 0 12px 40px rgba(37,84,184,0.15) !important; transform:translateY(-3px) !important; }
        .t-btn { transition: all 0.2s ease; }
        .t-btn:hover { filter:brightness(1.1); transform:translateY(-1px); box-shadow:0 6px 20px rgba(37,84,184,0.3); }
        .t-btn:active { transform:scale(0.97); }
        .t-tr:hover td { background:#f5f8ff !important; }
        .t-row-anim { animation: fadeUp 0.3s ease both; }
        .t-stat:hover { transform:translateY(-4px); box-shadow:0 16px 40px rgba(37,84,184,0.16) !important; }
        .t-stat { transition:all 0.28s cubic-bezier(.4,0,.2,1); }
        .status-dot { width:8px; height:8px; border-radius:50%; display:inline-block; margin-right:6px; }
        .status-dot.active { background:#16a34a; box-shadow:0 0 0 3px rgba(22,163,74,0.2); animation:pulse 2s infinite; }
        .gradient-text { background:linear-gradient(135deg,#1a3c8f,#3b82f6); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        .page-enter { animation: fadeUp 0.35s cubic-bezier(.4,0,.2,1) both; }
        .leave-card { transition:all 0.2s; cursor:pointer; }
        .leave-card:hover { transform:translateY(-3px); box-shadow:0 8px 28px rgba(37,84,184,0.15) !important; }
        .leave-card.selected { border-color:#2554b8 !important; background:linear-gradient(135deg,#f0f5ff,#e8eeff) !important; }
        .tab-btn { transition:all 0.2s; }
        .tab-btn:hover { background:rgba(37,84,184,0.08); color:#2554b8; }
        .form-inp:focus { border-color:#2554b8; box-shadow:0 0 0 3px rgba(37,84,184,0.12); }
        .enroll-card { transition:all 0.25s; }
        .enroll-card:hover { transform:translateY(-4px); box-shadow:0 12px 36px rgba(37,84,184,0.14) !important; }

        /* ── react-big-calendar modern overrides ── */
        .rbc-calendar { font-family:'Plus Jakarta Sans',sans-serif; }
        .rbc-toolbar { padding:12px 16px; background:linear-gradient(135deg,#f8faff,#eef2ff); border-bottom:1px solid #e2e8f6; flex-wrap:wrap; gap:8px; }
        .rbc-toolbar button { border-radius:8px; border:1.5px solid #e2e8f6; background:#fff; color:#2554b8; font-weight:600; font-size:12px; padding:6px 14px; cursor:pointer; transition:all 0.18s; font-family:inherit; }
        .rbc-toolbar button:hover { background:#2554b8; color:#fff; border-color:#2554b8; }
        .rbc-toolbar button.rbc-active { background:#2554b8 !important; color:#fff !important; border-color:#2554b8 !important; box-shadow:0 3px 10px rgba(37,84,184,0.3); }
        .rbc-toolbar button.rbc-active:hover { filter:brightness(1.1); }
        .rbc-toolbar-label { font-weight:800; font-size:16px; color:#0f1f45; }
        .rbc-header { padding:10px 4px; background:#f8faff; font-weight:700; font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:0.6px; border-bottom:1px solid #e2e8f6 !important; }
        .rbc-month-view { border:none; border-top:1px solid #e2e8f6; }
        .rbc-month-row { border-top:1px solid #f1f5f9; }
        .rbc-row-content { height:100%; }
        .rbc-month-view .rbc-event { margin:1px 2px !important; padding:3px 7px !important; }
        .rbc-day-bg { border-left:1px solid #f1f5f9 !important; }
        .rbc-today { background:rgba(37,84,184,0.05) !important; }
        .rbc-off-range-bg { background:#fafbfe !important; }
        .rbc-off-range .rbc-date-cell a { color:#cbd5e1; }
        .rbc-date-cell { padding:6px 10px; font-size:12px; font-weight:600; color:#475569; }
        .rbc-date-cell.rbc-now a { color:#2554b8; font-weight:800; }
        .rbc-event { overflow:hidden !important; border:none !important; padding:4px 8px !important; border-radius:6px !important; box-shadow:0 2px 6px rgba(0,0,0,0.18) !important; }
        .rbc-event:focus { outline:none !important; }
        .rbc-event.rbc-selected { opacity:0.92; box-shadow:0 4px 12px rgba(0,0,0,0.28) !important; }
        .rbc-evt-scheduled { background-color:#2554b8 !important; }
        .rbc-evt-completed { background-color:#16a34a !important; }
        .rbc-evt-cancelled { background-color:#dc2626 !important; }
        .rbc-event-content { overflow:hidden !important; width:100% !important; }
        .rbc-event-label { font-size:10px !important; font-weight:600 !important; opacity:0.9; white-space:nowrap; }
        .rbc-time-view .rbc-event { min-height:36px !important; }
        .rbc-show-more { color:#2554b8; font-size:11px; font-weight:700; background:rgba(37,84,184,0.1); padding:2px 10px; border-radius:20px; border:1px solid rgba(37,84,184,0.2); cursor:pointer; display:inline-block; margin:1px 2px; }
        .rbc-show-more:hover { background:rgba(37,84,184,0.18); }
        .rbc-time-view { border:none; border-top:1px solid #e2e8f6; }
        .rbc-time-header { background:#f8faff; border-bottom:1px solid #e2e8f6; }
        .rbc-time-header-content { border-left:1px solid #e2e8f6; }
        .rbc-time-content { border-top:1px solid #e2e8f6; }
        .rbc-timeslot-group { border-bottom:1px solid #f8faff; }
        .rbc-time-slot { font-size:11px; color:#94a3b8; font-weight:500; }
        .rbc-time-column .rbc-timeslot-group { border-bottom:1px solid #f1f5f9; }
        .rbc-current-time-indicator { background:#ef4444; height:2px; border-radius:2px; }
        .rbc-agenda-view table.rbc-agenda-table { border-radius:10px; overflow:hidden; border:1px solid #e2e8f6; }
        .rbc-agenda-view table.rbc-agenda-table thead { background:#f8faff; }
        .rbc-agenda-view table.rbc-agenda-table thead th { padding:10px 16px; font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:0.5px; }
        .rbc-agenda-view table.rbc-agenda-table tbody > tr > td { padding:10px 16px; font-size:13px; border-top:1px solid #f1f5f9; }
        .rbc-agenda-event-cell { cursor:pointer; }
        .rbc-slot-selection { background:rgba(37,84,184,0.1); border:1px solid rgba(37,84,184,0.3); }
      `}</style>

      <div className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`} style={{
        width: 248, background: 'linear-gradient(175deg, #1a3c8f 0%, #0f2460 60%, #0a1a45 100%)',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        boxShadow: '4px 0 32px rgba(10,26,69,0.35)',
      }}>
        {/* Decorative blobs */}
        <div style={{ position:'absolute', top:-40, right:-40, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:80, left:-30, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.03)', pointerEvents:'none' }} />

        {/* Brand */}
        <div style={{ padding:'28px 22px 20px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:46, height:46, borderRadius:14, background:'#ffffff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, border:'1px solid rgba(255,255,255,0.3)', overflow:'hidden', padding:4 }}>
              <img src="/logo.png" alt="logo" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
            </div>
            <div>
              <div style={{ color:'#fff', fontWeight:800, fontSize:13, letterSpacing:0.3 }}>Sapience Edu Connect</div>
              <div style={{ color:'rgba(255,255,255,0.45)', fontSize:9.5, fontWeight:500, marginTop:1 }}>Connecting minds shaping futures</div>
              <LiveClock />
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:'14px 10px', overflowY:'auto' }}>
          {NAV.map((item, i) => {
            const isActive = active === item.id;
            return (
              <div key={item.id} className={`t-nav-item ${isActive ? 't-active' : ''}`}
                onClick={() => { setActive(item.id); setSidebarOpen(false); }}
                onMouseEnter={() => setHovered(item.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  display:'flex', alignItems:'center', gap:12, padding:'11px 16px',
                  borderRadius:12, margin:'2px 0', cursor:'pointer',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                  fontWeight: isActive ? 700 : 500, fontSize:13.5,
                  animation: `fadeUp 0.3s ease ${i * 0.04}s both`,
                }}>
                <span style={{ fontSize:17, width:22, textAlign:'center', transition:'transform 0.2s', transform: hovered === item.id ? 'scale(1.2)' : 'scale(1)' }}>{item.icon}</span>
                <span style={{ flex:1 }}>{item.label}</span>
                {item.id === 'chat' && unreadCount > 0 && !isActive && (
                  <span style={{ background:'#dc2626', color:'#fff', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:800, flexShrink:0 }}>{unreadCount}</span>
                )}
                {isActive && <div style={{ marginLeft:'auto', width:6, height:6, borderRadius:'50%', background:'#93c5fd', flexShrink:0 }} />}
              </div>
            );
          })}
          {/* Signout */}
          <div className="t-nav-item" onClick={() => setShowSignout(true)}
            style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px', borderRadius:12, margin:'10px 0 2px', cursor:'pointer', color:'rgba(252,165,165,0.8)', fontWeight:500, fontSize:13.5, borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:16, marginTop:14 }}>
            <span style={{ fontSize:16 }}>⏻</span> Signout
          </div>
        </nav>

        {/* User chip */}
        <div style={{ padding:'14px 14px 18px', borderTop:'1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'rgba(255,255,255,0.08)', borderRadius:14, border:'1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#60a5fa,#93c5fd)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:15, color:'#1a3c8f', flexShrink:0 }}>
              {name[0]?.toUpperCase()}
            </div>
            <div style={{ flex:1, overflow:'hidden' }}>
              <div style={{ color:'#fff', fontWeight:700, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
              <div style={{ display:'flex', alignItems:'center', fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:1 }}>
                <span className="status-dot active" /> Online
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Signout modal */}
      {showSignout && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
          onClick={() => setShowSignout(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:24, padding:40, width:360, textAlign:'center', boxShadow:'0 30px 80px rgba(0,0,0,0.2)', animation:'bounceIn 0.3s ease' }}>
            <div style={{ fontSize:52, marginBottom:12 }}>👋</div>
            <h3 style={{ fontSize:20, fontWeight:800, color:'#0f1f45', marginBottom:8 }}>Sign Out?</h3>
            <p style={{ color:'#64748b', marginBottom:28, fontSize:14 }}>Are you sure you want to end your session?</p>
            <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
              <button className="t-btn" onClick={() => setShowSignout(false)}
                style={{ padding:'10px 24px', borderRadius:12, border:'2px solid #e2e8f6', background:'#fff', color:'#64748b', fontWeight:700, cursor:'pointer', fontSize:14 }}>Cancel</button>
              <button className="t-btn" onClick={logout}
                style={{ padding:'10px 24px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(220,38,38,0.35)' }}>Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   DASHBOARD
═══════════════════════════════════════════════════ */
function Dashboard({ setActive }) {
  const [schedules, setSchedules] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [payHistory, setPayHistory] = useState([]);
  const [emergencyStatus, setEmergencyStatus] = useState(null);
  const [pendingClassReqs, setPendingClassReqs] = useState(0);
  const [pendingAdminMsgs, setPendingAdminMsgs] = useState(0);
  const [emergencyModal, setEmergencyModal] = useState(false);
  const [emergencyScheduleId, setEmergencyScheduleId] = useState('');
  const [emergencyReason, setEmergencyReason] = useState('');
  const [emergencyMsg, setEmergencyMsg] = useState('');
  const upcomingRef = useRef(null);
  const name = localStorage.getItem('name') || 'Companion';

  useEffect(() => {
    API.get('/schedule/').then(r => setSchedules(r.data)).catch(() => {});
    API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {});
    API.get('/payments/my-payment-history').then(r => setPayHistory(r.data)).catch(() => {});
    API.get('/emergency/status').then(r => setEmergencyStatus(r.data)).catch(() => {});
    API.get('/class-request/for-tutor').then(r => {
      const pending = (r.data || []).filter(req => req.status === 'pending').length;
      setPendingClassReqs(pending);
    }).catch(() => {});
    API.get('/companion-queries/').then(r => {
      const pending = (r.data || []).filter(q => q.admin_initiated && q.status === 'pending').length;
      setPendingAdminMsgs(pending);
    }).catch(() => {});
  }, []);

  const upcoming = schedules
    .filter(s => s.status === 'scheduled' && new Date(`${s.date}T${s.start_time}+05:30`) >= new Date())
    .sort((a, b) => new Date(`${a.date}T${a.start_time}+05:30`) - new Date(`${b.date}T${b.start_time}+05:30`))
    .slice(0, 6);

  const stats = [
    { label: 'Total Students', value: [...new Map(enrollments.map(e=>[e.student_id,e])).values()].length, icon: '👥', color: '#2554b8', bg: '#eef3ff' },
    { label: 'Active Enrollments', value: enrollments.filter(e=>e.status==='active').length, icon: '✅', color: '#16a34a', bg: '#f0fdf4' },
    { label: 'Upcoming Classes', value: upcoming.length, icon: '📅', color: '#d97706', bg: '#fffbeb' },
    { label: 'Subjects', value: [...new Set(enrollments.map(e=>e.subject))].length, icon: '📚', color: '#7c3aed', bg: '#f5f3ff' },
  ];

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      {/* Greeting banner */}
      <div style={{ borderRadius:22, padding:'28px 32px', marginBottom:28, background:'linear-gradient(135deg,#1a3c8f 0%,#2554b8 50%,#3b82f6 100%)', backgroundSize:'200% 200%', animation:'gradientShift 8s ease infinite', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-30, right:-30, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.07)' }} />
        <div style={{ position:'absolute', bottom:-20, right:80, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.05)' }} />
        <div style={{ position:'relative', zIndex:1 }}>
          <p style={{ color:'rgba(255,255,255,0.7)', fontSize:13, fontWeight:600, marginBottom:6 }}>👋 Welcome back,</p>
          <h2 style={{ color:'#fff', fontSize:26, fontWeight:800, letterSpacing:-0.5, marginBottom:6 }}>{name}</h2>
          <p style={{ color:'rgba(255,255,255,0.65)', fontSize:13 }}>Here's what's happening with your classes today.</p>
        </div>
      </div>

      {/* Emergency & Class Request Notices */}
      <div style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap' }}>
        {/* Emergency status */}
        {emergencyStatus !== null && (
          emergencyStatus?.emergency_enabled ? (
            <button className="t-btn" onClick={() => { setEmergencyModal(true); setEmergencyMsg(''); setEmergencyScheduleId(''); setEmergencyReason(''); }}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 22px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:'0 4px 14px rgba(220,38,38,0.35)' }}>
              🚨 Emergency Cancel
            </button>
          ) : (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 22px', borderRadius:14, background:'#f1f5f9', color:'#94a3b8', fontWeight:700, fontSize:13, border:'1.5px solid #e2e8f0' }}>
              🚫 Emergency unavailable — contact admin
            </div>
          )
        )}
        {/* Class request badge */}
        {pendingClassReqs > 0 && (
          <button className="t-btn" onClick={() => setActive('classreq')}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 22px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#d97706,#f59e0b)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:'0 4px 14px rgba(217,119,6,0.35)' }}>
            📋 {pendingClassReqs} Pending Class Request{pendingClassReqs > 1 ? 's' : ''}
          </button>
        )}
        {/* Admin message badge */}
        {pendingAdminMsgs > 0 && (
          <button className="t-btn" onClick={() => setActive('queries')}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 22px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#1e40af,#3b82f6)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:'0 4px 14px rgba(30,64,175,0.35)' }}>
            📨 {pendingAdminMsgs} Message{pendingAdminMsgs > 1 ? 's' : ''} from Admin
          </button>
        )}
      </div>

      {/* Emergency cancel modal */}
      {emergencyModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
          onClick={() => setEmergencyModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:36, width:440, boxShadow:'0 30px 80px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize:18, fontWeight:800, color:'#dc2626', marginBottom:8 }}>🚨 Emergency Cancel</h3>
            <p style={{ fontSize:13, color:'#64748b', marginBottom:12 }}>Pick any class on the day you can't teach. <strong style={{ color:'#dc2626' }}>All your classes scheduled on that date will be cancelled.</strong></p>
            <div style={{ background:'rgba(220,38,38,0.06)', border:'1px solid rgba(220,38,38,0.25)', borderRadius:8, padding:'8px 12px', marginBottom:16, fontSize:12, color:'#b91c1c', fontWeight:600 }}>
              ⚠️ Emergency cancel affects your whole day — every scheduled class on that date is cancelled, and emergency access is used up until admin re-enables it.
            </div>
            {emergencyMsg && (
              <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8, background: emergencyMsg.startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', color: emergencyMsg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight:700, fontSize:13 }}>
                {emergencyMsg}
              </div>
            )}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Scheduled Class</label>
              <select value={emergencyScheduleId} onChange={e => setEmergencyScheduleId(e.target.value)}
                style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, boxSizing:'border-box' }}>
                <option value="">— Select a class —</option>
                {upcoming.map(s => (
                  <option key={s.id} value={s.id}>{s.subject} · {fmtDate(s.date)} {fmtTime(s.start_time)} · {s.student_name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Reason</label>
              <textarea value={emergencyReason} onChange={e => setEmergencyReason(e.target.value)}
                rows={3} placeholder="Briefly describe why you need to cancel…"
                style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="t-btn" onClick={() => setEmergencyModal(false)}
                style={{ padding:'10px 22px', borderRadius:12, border:'2px solid #e2e8f6', background:'#fff', color:'#64748b', fontWeight:700, cursor:'pointer', fontSize:13 }}>Cancel</button>
              <button className="t-btn"
                style={{ padding:'10px 22px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, boxShadow:'0 4px 14px rgba(220,38,38,0.35)' }}
                onClick={async () => {
                  if (!emergencyScheduleId) { setEmergencyMsg('Please select a class'); return; }
                  if (!emergencyReason.trim()) { setEmergencyMsg('Please provide a reason'); return; }
                  try {
                    await API.post('/emergency/use', { schedule_id: parseInt(emergencyScheduleId), reason: emergencyReason.trim() });
                    setEmergencyMsg('✓ Emergency cancel used. Contact admin to re-enable.');
                    setEmergencyStatus({ emergency_enabled: false });
                  } catch (e) {
                    setEmergencyMsg(e.response?.data?.detail || 'Error processing emergency cancel');
                  }
                }}>
                Confirm Emergency Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:28 }}>
        {stats.map((s, i) => (
          <div key={s.label} className="t-stat" onClick={() => s.label.includes('Student') && setActive('enrollment')}
            style={{ background:'#fff', borderRadius:18, padding:'20px 22px', border:'1px solid #e8edf8', boxShadow:'0 4px 16px rgba(37,84,184,0.07)', cursor:'pointer', animation:`fadeUp 0.35s ease ${i*0.07}s both` }}>
            <div style={{ width:48, height:48, borderRadius:14, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, marginBottom:14 }}>{s.icon}</div>
            <div style={{ fontSize:30, fontWeight:800, color:s.color, lineHeight:1 }}>{s.value}</div>
            <div style={{ fontSize:12, color:'#64748b', fontWeight:600, marginTop:5, textTransform:'uppercase', letterSpacing:0.5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick action cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18, marginBottom:28 }}>
        {[
          { label:'Upcoming Class', sub: upcoming[0] ? `${upcoming[0].subject} · ${fmtDate(upcoming[0].date)}` : 'No upcoming classes', detail: upcoming[0] ? `${fmtTime(upcoming[0].start_time)} · ${upcoming[0].student_name}` : 'Enjoy your free time!', grad:'linear-gradient(135deg,#2554b8,#60a5fa)', icon:'👨‍🏫', page:'schedule' },
          { label:'View Attendance', sub:'Track your class records', detail:'Mark and review attendance', grad:'linear-gradient(135deg,#7c3aed,#a78bfa)', icon:'📊', page:'attendance' },
        ].map(c => (
          <div key={c.label}
            onClick={() => c.label === 'Upcoming Class' ? upcomingRef.current?.scrollIntoView({ behavior:'smooth', block:'start' }) : setActive(c.page)}
            style={{ background:c.grad, borderRadius:20, padding:'26px 28px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', boxShadow:`0 8px 28px rgba(37,84,184,0.25)`, transition:'all 0.25s', minHeight:140 }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 16px 40px rgba(37,84,184,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 8px 28px rgba(37,84,184,0.25)'; }}>
            <div>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>{c.label}</p>
              <h3 style={{ color:'#fff', fontSize:16, fontWeight:800, marginBottom:5 }}>{c.sub}</h3>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize:12 }}>{c.detail}</p>
            </div>
            <div style={{ fontSize:56, opacity:0.85, flexShrink:0 }}>{c.icon}</div>
          </div>
        ))}
      </div>

      {/* Payment History card */}
      <div style={{ ...card(), padding:0, overflow:'hidden', marginBottom:20 }}>
        <div style={{ padding:'18px 26px', borderBottom:'1px solid #eef2fa', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h3 className="gradient-text" style={{ fontSize:16, fontWeight:800 }}>💰 My Payment History</h3>
        </div>
        {payHistory.length === 0 ? (
          <div style={{ textAlign:'center', padding:'28px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:36, marginBottom:8 }}>💳</div>
            <p style={{ fontWeight:600, fontSize:13 }}>No payments recorded yet</p>
          </div>
        ) : (
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:420 }}>
              <thead>
                <tr style={{ background:'#f8faff' }}>
                  {['Period','Hours','Amount','Paid On'].map(h => (
                    <th key={h} style={{ padding:'10px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payHistory.map((r, i) => (
                  <tr key={r.id} style={{ borderTop:'1px solid #eef2fa' }}>
                    <td style={{ padding:'12px 20px', fontSize:13, color:'#475569', whiteSpace:'nowrap' }}>{r.start_date} → {r.end_date}</td>
                    <td style={{ padding:'12px 20px', fontSize:13, color:'#475569', whiteSpace:'nowrap' }}>{r.total_hours}h</td>
                    <td style={{ padding:'12px 20px', fontWeight:800, fontSize:15, color:'#16a34a', whiteSpace:'nowrap' }}>₹{r.total_amount.toFixed(2)}</td>
                    <td style={{ padding:'12px 20px', fontSize:12, color:'#94a3b8', fontFamily:'monospace', whiteSpace:'nowrap' }}>{r.paid_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upcoming schedule */}
      <div ref={upcomingRef} style={{ ...card(), padding:0, overflow:'hidden' }}>
        <div style={{ padding:'20px 26px', borderBottom:'1px solid #eef2fa', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h3 className="gradient-text" style={{ fontSize:18, fontWeight:800 }}>Upcoming Schedule</h3>
          <button className="t-btn" onClick={() => setActive('schedule')}
            style={{ padding:'7px 18px', borderRadius:10, border:'1.5px solid #2554b8', background:'transparent', color:'#2554b8', fontWeight:700, cursor:'pointer', fontSize:12 }}>View All →</button>
        </div>
        {upcoming.length === 0 ? (
          <div style={{ textAlign:'center', padding:'48px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:44, marginBottom:12 }}>🎉</div>
            <p style={{ fontWeight:600 }}>You have no upcoming schedules. Enjoy your free time!</p>
          </div>
        ) : (
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:480 }}>
              <thead>
                <tr style={{ background:'#f8faff' }}>
                  {['Subject','Student','Date','Time','Status'].map(h => (
                    <th key={h} style={{ padding:'12px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {upcoming.map((s, i) => (
                  <tr key={s.id} className="t-tr t-row-anim" style={{ borderTop:'1px solid #eef2fa', animationDelay:`${i*0.05}s` }}>
                    <td style={{ padding:'13px 20px', fontWeight:700, color:'#1e293b', whiteSpace:'nowrap' }}>{s.subject}</td>
                    <td style={{ padding:'13px 20px', color:'#475569', whiteSpace:'nowrap' }}>{s.student_name||'—'}</td>
                    <td style={{ padding:'13px 20px', color:'#475569', fontFamily:'monospace', fontSize:13, whiteSpace:'nowrap' }}>{fmtDate(s.date)}</td>
                    <td style={{ padding:'13px 20px', color:'#475569', fontFamily:'monospace', fontSize:13, whiteSpace:'nowrap' }}>{fmtTime(s.start_time)}</td>
                    <td style={{ padding:'13px 20px' }}>
                      <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, whiteSpace:'nowrap', background:'rgba(37,84,184,0.1)', color:'#2554b8', border:'1px solid rgba(37,84,184,0.2)' }}>{s.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MY PROFILE
═══════════════════════════════════════════════════ */
function MyProfile() {
  const [profile, setProfile] = useState(null);
  useEffect(() => { API.get('/auth/me').then(r => setProfile(r.data)).catch(() => {}); }, []);

  if (!profile) return <LoadingState />;
  const subjects = profile.selected_subjects ? profile.selected_subjects.split(',').map(s => s.trim()).filter(Boolean) : [];
  const subjectColors = ['#2554b8','#7c3aed','#16a34a','#d97706','#dc2626','#0891b2'];

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      <div style={{ display:'grid', gridTemplateColumns:'min(340px, 100%) 1fr', gap:24 }} className="profile-grid">
        {/* Left — identity card */}
        <div style={{ ...card({ padding:0, overflow:'hidden' }) }}>
          <div style={{ background:'linear-gradient(135deg,#1a3c8f,#3b82f6)', padding:'36px 28px 28px', textAlign:'center' }}>
            <div style={{ width:88, height:88, borderRadius:'50%', background:'rgba(255,255,255,0.2)', border:'4px solid rgba(255,255,255,0.4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36, fontWeight:800, color:'#fff', margin:'0 auto 16px', backdropFilter:'blur(8px)' }}>
              {profile.name?.[0]?.toUpperCase()}
            </div>
            <h2 style={{ color:'#fff', fontWeight:800, fontSize:20, marginBottom:6 }}>{profile.name}</h2>
            {profile.uid && <span style={{ display:'inline-block', background:'rgba(255,255,255,0.2)', color:'#fff', fontSize:12, fontWeight:700, padding:'3px 14px', borderRadius:20, marginBottom:8, border:'1px solid rgba(255,255,255,0.3)' }}>{profile.uid}</span>}
            <p style={{ color:'rgba(255,255,255,0.7)', fontSize:13 }}>🎓 Companion ·{profile.gender||'N/A'}</p>
          </div>
          <div style={{ padding:'20px 24px' }}>
            {[['📧 Email', profile.email],['📱 Phone', profile.phone||'Not provided'],['🏙️ City', profile.city||'Not provided']].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f1f5f9' }}>
                <span style={{ fontSize:13, color:'#64748b', fontWeight:600 }}>{k}</span>
                <span style={{ fontSize:13, color:'#1e293b', fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ padding:'0 24px 20px' }}>
            <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#16a34a', fontWeight:700 }}>
              <span className="status-dot active" style={{ width:8, height:8 }} />Active Companion
            </span>
          </div>
        </div>

        {/* Right */}
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          {/* Bio */}
          <div style={card()}>
            <h4 style={{ fontSize:13, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:12 }}>Biography</h4>
            <p style={{ fontSize:14, color: profile.bio ? '#1e293b' : '#94a3b8', lineHeight:1.8, background:'#f8faff', padding:'14px 18px', borderRadius:12, border:'1px solid #eef2fa' }}>
              {profile.bio || 'No biography added. Contact admin to update.'}
            </p>
          </div>

          {/* Contact details */}
          <div style={card()}>
            <h4 style={{ fontSize:13, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:14 }}>Contact Details</h4>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
              {[['Address', profile.address||'Not provided'],['Alt Email', profile.alt_email||'Not provided'],['Contact No.', profile.phone||'Not provided']].map(([k,v]) => (
                <div key={k} style={{ background:'#f8faff', padding:'14px 16px', borderRadius:12, border:'1px solid #eef2fa' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:5 }}>{k}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#1e293b' }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Subjects */}
          <div style={card()}>
            <h4 style={{ fontSize:13, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:14 }}>Preferred Subjects</h4>
            <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
              {subjects.length === 0 ? <p style={{ color:'#94a3b8', fontSize:13 }}>No subjects assigned</p> : subjects.map((s,i) => (
                <span key={s} style={{ padding:'7px 18px', borderRadius:50, fontWeight:700, fontSize:13, color:'#fff', background:subjectColors[i%subjectColors.length], boxShadow:`0 3px 10px ${subjectColors[i%subjectColors.length]}44` }}>{s}</span>
              ))}
            </div>
          </div>

          <div style={{ padding:'12px 18px', background:'linear-gradient(135deg,#fffbeb,#fef3c7)', borderRadius:12, border:'1px solid #fde68a', fontSize:13, color:'#92400e', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
            ⚠️ You cannot edit your profile. Please contact the admin for any changes.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   ENROLLMENT LIST
═══════════════════════════════════════════════════ */
function EnrollmentList() {
  const [enrollments, setEnrollments] = useState([]);
  const [filter, setFilter] = useState('all');
  useEffect(() => { API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {}); }, []);
  const filtered = filter === 'all' ? enrollments : enrollments.filter(e => e.status === filter);

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      <div style={{ display:'flex', gap:8, marginBottom:22 }}>
        {['all','active','completed','paused'].map(f => (
          <button key={f} style={pill(filter===f)} onClick={() => setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>
        ))}
      </div>
      {filtered.length === 0 ? <EmptyState icon="👥" text="No students found" /> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }}>
          {filtered.map((e,i) => (
            <div key={e.id} className="enroll-card t-row-anim"
              style={{ background:'#fff', borderRadius:20, border:'1px solid #e8edf8', overflow:'hidden', boxShadow:'0 4px 16px rgba(37,84,184,0.07)', animationDelay:`${i*0.07}s` }}>
              {/* Top color bar */}
              <div style={{ height:5, background: e.status==='active' ? 'linear-gradient(90deg,#16a34a,#4ade80)' : e.status==='completed' ? 'linear-gradient(90deg,#2554b8,#60a5fa)' : 'linear-gradient(90deg,#d97706,#fbbf24)' }} />
              <div style={{ padding:'18px 22px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                  <div style={{ width:52, height:52, borderRadius:'50%', background:'linear-gradient(135deg,#dbeafe,#bfdbfe)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>👤</div>
                  <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:700, background: e.status==='active' ? '#f0fdf4' : '#f0f5ff', color: e.status==='active' ? '#16a34a' : '#2554b8', border:`1px solid ${e.status==='active' ? 'rgba(22,163,74,0.2)' : 'rgba(37,84,184,0.2)'}` }}>
                    {e.status.charAt(0).toUpperCase()+e.status.slice(1)}
                  </span>
                </div>
                <h4 style={{ fontWeight:800, fontSize:16, color:'#0f1f45', marginBottom:3 }}>{e.student_name||`Student #${e.student_id}`}</h4>
                <p style={{ fontSize:12, color:'#64748b', marginBottom:14 }}>👥 Student</p>
                <div style={{ background:'#f8faff', borderRadius:10, padding:'12px 14px', border:'1px solid #eef2fa' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, fontWeight:700, color:'#1e293b', marginBottom:5 }}>📚 {e.subject}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#64748b' }}>🗓 Started: {fmtDate(e.start_date)}</div>
                </div>
                {e.has_plan && (
                  <button
                    onClick={async () => {
                      try {
                        const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
                        const response = await API.get(`/enrollment/${e.id}/plan`, { responseType: 'blob' });
                        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = e.plan_file_name || 'student-plan';
                        document.body.appendChild(a); a.click();
                        document.body.removeChild(a); window.URL.revokeObjectURL(url);
                      } catch { alert('Could not download student plan.'); }
                    }}
                    style={{ marginTop:10, width:'100%', padding:'8px', borderRadius:10, border:'1.5px solid #86efac', background:'#f0fdf4', color:'#16a34a', fontWeight:700, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    📄 View Student Plan
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   ATTENDANCE
═══════════════════════════════════════════════════ */
function Attendance() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  useEffect(() => { API.get('/class/logs').then(r => setLogs(r.data)).catch(() => {}); }, []);
  const filtered = filter === 'all' ? logs : logs.filter(l => l.status === filter);

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      <div style={{ display:'flex', gap:8, marginBottom:22 }}>
        {['all','completed','cancelled'].map(f => (
          <button key={f} style={pill(filter===f)} onClick={() => setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>
        ))}
        <span style={{ marginLeft:'auto', fontSize:13, color:'#64748b', fontWeight:600, alignSelf:'center' }}>{filtered.length} records</span>
      </div>
      <div style={card({ padding:0, overflow:'hidden' })}>
        {filtered.length === 0 ? <EmptyState icon="📋" text="No attendance records found" /> : (
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:500 }}>
              <thead>
                <tr style={{ background:'#f8faff' }}>
                  {['Student','Subject','Status','Topics Covered','Date'].map(h => (
                    <th key={h} style={{ padding:'13px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l,i) => (
                  <tr key={l.id} className="t-tr t-row-anim" style={{ borderTop:'1px solid #f1f5f9', animationDelay:`${i*0.05}s` }}>
                    <td style={{ padding:'13px 20px', fontWeight:700, whiteSpace:'nowrap' }}>{l.student_name||'—'}</td>
                    <td style={{ padding:'13px 20px', whiteSpace:'nowrap' }}>{l.subject||'—'}</td>
                    <td style={{ padding:'13px 20px' }}>
                      <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, whiteSpace:'nowrap', background: l.status==='completed' ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)', color: l.status==='completed' ? '#16a34a' : '#dc2626', border:`1px solid ${l.status==='completed' ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)'}` }}>{l.status}</span>
                    </td>
                    <td style={{ padding:'13px 20px', maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#475569', fontSize:13 }}>{l.topics||'—'}</td>
                    <td style={{ padding:'13px 20px', color:'#94a3b8', fontSize:12, fontFamily:'monospace', whiteSpace:'nowrap' }}>{l.schedule_date || l.updated_at?.slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CLASS UPDATE MODAL
═══════════════════════════════════════════════════ */
function ClassModal({ session, onClose, onDone }) {
  const [status, setStatus] = useState('completed');
  const [form, setForm] = useState({ topics:'', homework:'', homework_due_date:'', remarks:'', cancel_reason:'' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (status === 'completed' && !form.topics.trim()) { setErr('Please fill in Topics Covered'); return; }
    if (status === 'completed' && !form.homework.trim()) { setErr('Please fill in Homework Assigned'); return; }
    if (form.remarks.trim().length < 50) { setErr(`Remarks must be at least 50 characters (currently ${form.remarks.trim().length})`); return; }
    if (status === 'cancelled' && !form.cancel_reason.trim()) { setErr('Please provide a cancellation reason'); return; }
    setLoading(true); setErr('');
    try {
      const payload = {
        schedule_id: session.id, status, ...form,
        homework_due_date: form.homework_due_date || null,
      };
      if (status === 'cancelled' && form.cancel_reason.trim()) {
        payload.cancel_reason = 'student_absent';
      }
      await API.post('/class/update', payload);
      onDone(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000, padding:'16px' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:'#fff', borderRadius:24, width:'100%', maxWidth:500, maxHeight:'88vh', display:'flex', flexDirection:'column', boxShadow:'0 30px 80px rgba(0,0,0,0.18)', animation:'bounceIn 0.3s ease' }}>

        <div style={{ flexShrink:0, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 24px 14px', borderBottom:'1px solid #f1f5f9', borderRadius:'24px 24px 0 0', background:'#fff' }}>
          <h3 style={{ fontWeight:800, fontSize:17, color:'#0f1f45', margin:0 }}>✏️ Update Class — {session.subject}</h3>
          <button onClick={onClose}
            style={{ width:36, height:36, borderRadius:10, border:'none', background:'#f1f5f9', cursor:'pointer', fontSize:20, display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b', flexShrink:0, marginLeft:12 }}>×</button>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px 24px' }}>
          <div style={{ background:'linear-gradient(135deg,#f0f5ff,#e8eeff)', borderRadius:12, padding:'14px 18px', marginBottom:20, border:'1px solid #dbeafe' }}>
            <div style={{ display:'flex', gap:16, fontSize:13, color:'#2554b8', fontWeight:600, flexWrap:'wrap' }}>
              <span>📅 {fmtDate(session.date)}</span>
              <span>⏰ {fmtTime(session.start_time)} – {fmtTime(session.end_time)}</span>
            </div>
            {session.student_name && <div style={{ fontSize:13, color:'#3b82f6', marginTop:5 }}>👨‍🎓 {session.student_name}</div>}
          </div>

          {/* Important status-marking instructions */}
          <div style={{ background:'rgba(220,38,38,0.06)', border:'1.5px solid rgba(220,38,38,0.3)', borderRadius:12, padding:'12px 16px', marginBottom:20 }}>
            <div style={{ fontWeight:800, fontSize:13, color:'#dc2626', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>⚠️ IMPORTANT INSTRUCTION</div>
            <ul style={{ margin:0, paddingLeft:18, color:'#b91c1c', fontSize:12.5, lineHeight:1.6 }}>
              <li>Mark a class as <strong>Completed</strong> only if the session was actually conducted.</li>
              <li>Use <strong>Cancelled</strong> <u>only when the student was absent</u>. If the class did not happen because of <strong>your</strong> unavailability (Companion), do <strong>NOT</strong> use Cancelled.</li>
              <li>This is an <strong>automated system</strong> — attendance and payments are processed automatically, so incorrect status can cause <strong>payment delays</strong>.</li>
              <li>Please verify the class status before submitting.</li>
            </ul>
          </div>

          {err && <div style={{ background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.2)', borderRadius:10, padding:'11px 16px', color:'#dc2626', fontSize:13, fontWeight:600, marginBottom:16 }}>{err}</div>}

          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:10 }}>Class Status</label>
            <div style={{ display:'flex', gap:10 }}>
              {[['completed','✅ Completed','#16a34a'],['cancelled','❌ Cancelled','#dc2626']].map(([s,label,color]) => (
                <button key={s} onClick={() => setStatus(s)}
                  style={{ flex:1, padding:'12px', borderRadius:12, border:`2px solid ${status===s ? color : '#e2e8f6'}`, background: status===s ? `${color}10` : '#fff', color: status===s ? color : '#94a3b8', fontWeight:700, cursor:'pointer', fontSize:14, transition:'all 0.2s' }}>{label}</button>
              ))}
            </div>
          </div>

          {status === 'completed' && (
            <>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Topics Covered *</label>
                <textarea style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'vertical', minHeight:80, outline:'none', boxSizing:'border-box' }}
                  className="form-inp" placeholder="e.g. Chapter 5 – Quadratic Equations (factorisation, roots)…"
                  value={form.topics} onChange={e => setForm({...form,topics:e.target.value})} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Homework Assigned *</label>
                <textarea style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'vertical', minHeight:70, outline:'none', boxSizing:'border-box' }}
                  className="form-inp" placeholder="e.g. Exercise 3.2 Q1–10…"
                  value={form.homework} onChange={e => setForm({...form,homework:e.target.value})} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Homework Due Date <span style={{ fontWeight:400, textTransform:'none', color:'#94a3b8' }}>(optional)</span></label>
                <input type="date" style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, outline:'none', boxSizing:'border-box' }}
                  value={form.homework_due_date} onChange={e => setForm({...form, homework_due_date:e.target.value})} />
              </div>
            </>
          )}
          {status === 'cancelled' && (
            <div style={{ marginBottom:14, padding:'12px 16px', borderRadius:10, background:'rgba(220,38,38,0.05)', border:'1px solid rgba(220,38,38,0.15)' }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#dc2626', display:'block', marginBottom:7 }}>Cancellation Details *</label>
              <p style={{ fontSize:11, color:'#64748b', marginBottom:8 }}>Note: 0.5 hours will be deducted from the student's cycle for this cancellation (tutor attended but student was absent).</p>
              <textarea style={{ width:'100%', border:'1.5px solid rgba(220,38,38,0.3)', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'vertical', minHeight:80, outline:'none', boxSizing:'border-box' }}
                placeholder="Describe the cancellation reason (e.g. Student was not available, tutor attended)…"
                value={form.cancel_reason} onChange={e => setForm({...form, cancel_reason:e.target.value})} />
            </div>
          )}
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Remarks / Comments *</label>
            <textarea style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'vertical', minHeight:70, outline:'none', boxSizing:'border-box' }}
              className="form-inp" placeholder="Any notes for admin…"
              value={form.remarks} onChange={e => setForm({...form,remarks:e.target.value})} />
            <div style={{ fontSize:11, color: form.remarks.trim().length >= 50 ? '#16a34a' : '#94a3b8', textAlign:'right', marginTop:3 }}>
              {form.remarks.trim().length}/50 characters minimum
            </div>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button className="t-btn" onClick={onClose}
              style={{ flex:1, padding:'12px', borderRadius:12, border:'1.5px solid #e2e8f6', background:'#fff', color:'#64748b', fontWeight:700, cursor:'pointer', fontSize:14 }}>Cancel</button>
            <button className="t-btn" onClick={submit} disabled={loading}
              style={{ flex:2, padding:'12px', borderRadius:12, border:'none', background: status==='completed' ? 'linear-gradient(135deg,#16a34a,#22c55e)' : 'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow: status==='completed' ? '0 4px 14px rgba(22,163,74,0.35)' : '0 4px 14px rgba(220,38,38,0.35)', opacity: loading ? 0.7 : 1 }}>
              {loading ? '⏳ Saving…' : `Submit as ${status.charAt(0).toUpperCase()+status.slice(1)}`}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SCHEDULES
═══════════════════════════════════════════════════ */
const TUTOR_COLORS = { completed:'#16a34a', cancelled:'#dc2626', scheduled:'#2554b8' };

function CalEventTutor({ event }) {
  const icon = { completed:'✓', cancelled:'✕', scheduled:'◉' }[event.status] || '◉';
  return (
    <>
      <div style={{ fontWeight:700, fontSize:11, color:'#fff', lineHeight:'15px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {icon} {event.title}
      </div>
      {event.student_name && (
        <div style={{ fontSize:10, color:'rgba(255,255,255,0.88)', lineHeight:'13px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:1 }}>
          👤 {event.student_name}
        </div>
      )}
    </>
  );
}

function ScheduleView() {
  const [schedules, setSchedules] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [calHeight, setCalHeight] = useState(window.innerWidth <= 768 ? 520 : 680);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');
  const [waLoading, setWaLoading] = useState(false);
  const [waMsg, setWaMsg] = useState('');
  const [waSentIds, setWaSentIds] = useState(new Set());
  const [joinMsg, setJoinMsg] = useState('');
  const load = useCallback(() => { API.get('/schedule/').then(r => setSchedules(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setCalHeight(mobile ? 520 : 680);
      setIsMobile(mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const events = schedules.map(s => ({
    ...s, title: s.subject||'Class',
    start: new Date(`${s.date}T${s.start_time}+05:30`),
    end: new Date(`${s.date}T${s.end_time}+05:30`),
    resource: s,
  }));

  return (
    <div style={{ padding:'28px 32px', display:'flex', gap:22 }} className="page-enter schedule-wrap">
      <div style={{ flex:1, minWidth:0 }}>
        <div style={card({ padding:20 })}>
          <h3 className="gradient-text" style={{ textAlign:'center', fontSize:18, fontWeight:800, marginBottom:8 }}>My Schedule</h3>
          {/* Legend */}
          <div style={{ display:'flex', gap:14, justifyContent:'center', marginBottom:14, flexWrap:'wrap' }}>
            {[['#2554b8','◉ Scheduled'],['#16a34a','✓ Completed'],['#dc2626','✕ Cancelled']].map(([c,l])=>(
              <span key={l} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#64748b', fontWeight:600 }}>
                <span style={{ width:12, height:12, borderRadius:3, background:c, display:'inline-block', boxShadow:`0 2px 4px ${c}66` }} />{l}
              </span>
            ))}
          </div>
          <Calendar localizer={localizer} events={events} startAccessor="start" endAccessor="end" style={{ height:calHeight }}
            defaultView="month"
            views={['month','week','day','agenda']}
            components={{ event: CalEventTutor }}
            eventPropGetter={e => ({ className:`rbc-evt-${e.status||'scheduled'}` })}
            onSelectEvent={e => { setDetail(e.resource); setReminderMsg(''); setWaMsg(''); setJoinMsg(''); }}
            popup />
        </div>
      </div>

      {detail && (
        <>
          {isMobile && (
            <div style={{ position:'fixed', inset:0, zIndex:1999, background:'rgba(0,0,0,0.45)' }} onClick={() => setDetail(null)} />
          )}
          <div className={isMobile ? '' : 'schedule-detail-panel'}
            style={isMobile
              ? { position:'fixed', bottom:0, left:0, right:0, zIndex:2000, borderRadius:'20px 20px 0 0', padding:24, background:'#fff', maxHeight:'75vh', overflowY:'auto', boxShadow:'0 -8px 32px rgba(0,0,0,0.2)', animation:'fadeUp 0.25s ease' }
              : { width:300, flexShrink:0, animation:'fadeUp 0.25s ease' }}>
            <div style={isMobile ? {} : card({ position:'sticky', top:80, padding:24 })}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:800, color:'#2554b8' }}>Schedule Details</div>
                  <span style={{ display:'inline-block', marginTop:5, padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:700, background:`${TUTOR_COLORS[detail.status]}18`, color:TUTOR_COLORS[detail.status], border:`1px solid ${TUTOR_COLORS[detail.status]}30` }}>{detail.status}</span>
                  {detail.is_payable === false && (
                    <span style={{ display:'inline-block', marginTop:5, marginLeft:6, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:'#dc262618', color:'#dc2626', border:'1px solid #dc262630' }}>NOT PAYABLE</span>
                  )}
                </div>
                <button onClick={() => setDetail(null)} style={{ width:28, height:28, borderRadius:8, border:'none', background:'#f1f5f9', cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
              </div>
              {[['Title', detail.subject],['Student', detail.student_name||'—'],['Subject', detail.subject],['Date', fmtDate(detail.date)]].map(([k,v]) => (
                <div key={k} style={{ marginBottom:14 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#2554b8', textTransform:'uppercase', letterSpacing:0.8, marginBottom:3 }}>{k}</div>
                  <div style={{ fontWeight:600, color:'#0f1f45', fontSize:14 }}>{v}</div>
                </div>
              ))}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                {[['From', fmtTime(detail.start_time)],['To', fmtTime(detail.end_time)]].map(([k,v]) => (
                  <div key={k}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#2554b8', textTransform:'uppercase', letterSpacing:0.8, marginBottom:3 }}>{k}</div>
                    <div style={{ fontWeight:700, color:'#0f1f45', fontSize:15 }}>{v}</div>
                  </div>
                ))}
              </div>
              {detail.status === 'scheduled' && (
                <>
                  {/* Join Class button — online classes, available any time (no
                      start-time gate). Link resolved on click; a Teams meeting is
                      created on demand if it doesn't exist yet. */}
                  {detail.mode === 'online' && (
                    <button onClick={async () => {
                      const w = window.open('', '_blank');
                      try {
                        const r = await API.get(`/teams/join/${detail.id}`);
                        const url = r.data?.join_url;
                        if (url) { if (w) w.location.href = url; else window.location.href = url; }
                        else { if (w) w.close(); alert('Class link is not ready yet. Please try again in a moment.'); }
                      } catch (e) { if (w) w.close(); alert(e.response?.data?.detail || 'Class link is not ready yet. Please try again in a moment.'); }
                    }}
                      style={{ display:'block', width:'100%', padding:'11px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(22,163,74,0.35)', marginTop:4, textAlign:'center', boxSizing:'border-box' }}>
                      🔗 Join Class Now
                    </button>
                  )}
                  <button className="t-btn" onClick={() => { setSelected(detail); setDetail(null); }}
                    style={{ width:'100%', padding:'11px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(37,84,184,0.35)', marginTop:4 }}>
                    ✏️ Update Class
                  </button>
                  {detail.date === new Date().toISOString().slice(0, 10) && (
                    <button
                      disabled={reminderLoading}
                      onClick={async () => {
                        setReminderLoading(true); setReminderMsg('');
                        try {
                          await API.post(`/push/class-reminder/${detail.id}`);
                          setReminderMsg('✅ Reminder sent!');
                        } catch (e) {
                          const msg = e?.response?.data?.detail || 'Failed to send reminder';
                          setReminderMsg(msg.includes('429') || e?.response?.status === 429
                            ? '⚠️ Limit reached (max 2/hr per student)'
                            : `⚠️ ${msg}`);
                        } finally { setReminderLoading(false); }
                      }}
                      style={{ width:'100%', padding:'11px', borderRadius:12, border:'none', background: reminderLoading ? '#94a3b8' : 'linear-gradient(135deg,#f59e0b,#fbbf24)', color:'#fff', fontWeight:700, cursor: reminderLoading ? 'not-allowed' : 'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(245,158,11,0.35)', marginTop:10, display:'flex', alignItems:'center', justifyContent:'center', gap:6, minHeight:44 }}>
                      {reminderLoading ? '⏳ Sending…' : '🔔 Remind Student to Join'}
                    </button>
                  )}
                  {reminderMsg && (
                    <div style={{ marginTop:8, fontSize:12, fontWeight:600, color: reminderMsg.startsWith('✅') ? '#16a34a' : '#b45309', textAlign:'center', padding:'6px 10px', borderRadius:8, background: reminderMsg.startsWith('✅') ? '#f0fdf4' : '#fffbeb' }}>
                      {reminderMsg}
                    </div>
                  )}
                  {/* WhatsApp one-time reminder */}
                  {detail.date === new Date().toISOString().slice(0, 10) && (
                    <>
                      {(detail.whatsapp_reminder_sent || waSentIds.has(detail.id)) ? (
                        <div style={{ marginTop:10, width:'100%', padding:'11px', borderRadius:12, background:'#f0fdf4', color:'#16a34a', fontWeight:700, fontSize:13, textAlign:'center', border:'1.5px solid #86efac', minHeight:44, display:'flex', alignItems:'center', justifyContent:'center' }}>
                          ✅ WhatsApp Reminder Sent
                        </div>
                      ) : (
                        <button
                          disabled={waLoading}
                          onClick={async () => {
                            setWaLoading(true); setWaMsg('');
                            try {
                              await API.post(`/schedule/${detail.id}/whatsapp-reminder`);
                              setWaSentIds(prev => new Set([...prev, detail.id]));
                              setWaMsg('✅ WhatsApp reminder sent!');
                            } catch (e) {
                              const msg = e?.response?.data?.detail || 'Failed to send';
                              setWaMsg(`⚠️ ${msg}`);
                            } finally { setWaLoading(false); }
                          }}
                          style={{ width:'100%', padding:'11px', borderRadius:12, border:'none', background: waLoading ? '#94a3b8' : 'linear-gradient(135deg,#25d366,#128c7e)', color:'#fff', fontWeight:700, cursor: waLoading ? 'not-allowed' : 'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(37,211,102,0.35)', marginTop:10, display:'flex', alignItems:'center', justifyContent:'center', gap:6, minHeight:44 }}>
                          {waLoading ? '⏳ Sending…' : '📲 WhatsApp Reminder'}
                        </button>
                      )}
                      {waMsg && (
                        <div style={{ marginTop:8, fontSize:12, fontWeight:600, color: waMsg.startsWith('✅') ? '#16a34a' : '#b45309', textAlign:'center', padding:'6px 10px', borderRadius:8, background: waMsg.startsWith('✅') ? '#f0fdf4' : '#fffbeb' }}>
                          {waMsg}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
      {selected && <ClassModal session={selected} onClose={() => setSelected(null)} onDone={() => { load(); setDetail(null); }} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   TESTS & GRADES
═══════════════════════════════════════════════════ */
function TestsAndGrades() {
  const [tab, setTab] = useState('create');
  const [tests, setTests] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [myPapers, setMyPapers] = useState([]);
  const [rescheduleReqs, setRescheduleReqs] = useState([]);
  const [rescheduleReviewMsg, setRescheduleReviewMsg] = useState('');
  const emptyForm = { test_name:'', student_id:'', subject:'', topic:'', max_marks:'', class_name:'', test_date:'', start_time:'', end_time:'', exam_secret_code:'' };
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState('');
  const [gradeTest, setGradeTest] = useState(null);
  const [gradeEntries, setGradeEntries] = useState([]);
  const [gradeForm, setGradeForm] = useState({});
  const [gradeStudentSearch, setGradeStudentSearch] = useState('');
  const [gradeStudentId, setGradeStudentId] = useState('');
  const [gradeStudentTests, setGradeStudentTests] = useState([]);
  const [gradeStudentForm, setGradeStudentForm] = useState({});
  const [showGradeStuList, setShowGradeStuList] = useState(false);

  const loadTests = useCallback(() => { API.get('/tests/my').then(r => setTests(r.data)).catch(() => {}); }, []);
  const deleteTest = useCallback(async (t) => {
    if (!window.confirm(`Delete the test "${t.test_name}"? This also removes its grade entries and cannot be undone.`)) return;
    try {
      await API.delete(`/tests/${t.id}`);
      loadTests();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete test');
    }
  }, [loadTests]);
  const loadReschedule = useCallback(() => { API.get('/tests/reschedule-requests').then(r => setRescheduleReqs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    loadTests(); loadReschedule();
    API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {});
    API.get('/exams/my').then(r => setMyPapers(r.data)).catch(() => {});
  }, [loadTests, loadReschedule]);

  const reviewReschedule = async (id, status, response) => {
    try {
      await API.put(`/tests/reschedule-request/${id}/review`, { status, tutor_response: response });
      setRescheduleReviewMsg(`✓ Request ${status}`);
      loadReschedule();
    } catch { setRescheduleReviewMsg('Error updating request'); }
    setTimeout(() => setRescheduleReviewMsg(''), 3000);
  };

  const studentMap = {};
  enrollments.forEach(e => { if (!studentMap[e.student_id]) studentMap[e.student_id] = { id:e.student_id, name:e.student_name }; });
  const students = Object.values(studentMap);
  const studentSubs = enrollments.filter(e => String(e.student_id) === String(form.student_id));

  const handleStudent = (sid) => {
    const subs = enrollments.filter(e => String(e.student_id) === String(sid));
    setForm(f => ({ ...f, student_id:sid, subject: subs.length===1 ? subs[0].subject : '', class_name: subs[0]?.class_name||'' }));
  };
  const handleSubject = (sub) => {
    const enr = enrollments.find(e => String(e.student_id)===String(form.student_id) && e.subject===sub);
    setForm(f => ({ ...f, subject:sub, class_name: enr?.class_name||f.class_name }));
  };

  const createTest = async () => {
    if (!form.test_name||!form.student_id||!form.subject||!form.max_marks||!form.test_date) { setMsg('Fill all required fields'); return; }
    if (!form.exam_secret_code) { setMsg('Please select an exam paper'); return; }
    try {
      const res = await API.post('/tests/', { test_name:form.test_name, subject:form.subject, topic:form.topic, max_marks:parseInt(form.max_marks), class_name:form.class_name, test_date:form.test_date, start_time:form.start_time ? localTimeToIST(form.test_date, form.start_time) : null, end_time:form.end_time ? localTimeToIST(form.test_date, form.end_time) : null, exam_secret_code:form.exam_secret_code||null });
      await API.post('/tests/grade', { test_id: res.data.id, student_id: parseInt(form.student_id), marks_obtained: null, remarks: null });
      setMsg('✓ Test created successfully!'); setForm(emptyForm); loadTests();
    } catch (e) { setMsg(e.response?.data?.detail||'Failed'); }
  };

  // From Test List → click Grades: show just that test's grade entries
  const openGrades = async (test) => {
    setGradeTest(test); setTab('grades');
    // Clear student search mode
    setGradeStudentId(''); setGradeStudentSearch(''); setGradeStudentTests([]);
    try {
      const r = await API.get(`/tests/${test.id}/grades`);
      setGradeEntries(r.data);
      const gf = {}; r.data.forEach(g => { gf[g.student_id] = { marks: g.marks_obtained ?? '', remarks: g.remarks ?? '' }; });
      setGradeForm(gf);
    } catch {}
  };

  const saveGrade = async (sid, sname) => {
    const g = gradeForm[sid] || {};
    try {
      await API.post('/tests/grade', { test_id: gradeTest.id, student_id: sid, marks_obtained: g.marks !== '' ? parseInt(g.marks) : null, remarks: g.remarks || null });
      setMsg(`✓ Grade saved for ${sname}`);
      const r = await API.get(`/tests/${gradeTest.id}/grades`);
      setGradeEntries(r.data);
    } catch { setMsg('Failed to save grade'); }
  };

  // From Grades tab student search: show all tests for that student
  const selectGradeStudent = async (student) => {
    setGradeStudentId(student.id); setGradeStudentSearch(student.name); setShowGradeStuList(false);
    setGradeTest(null); setGradeEntries([]);
    try {
      const r = await API.get(`/tests/for-student/${student.id}`);
      setGradeStudentTests(r.data);
      const gf = {}; r.data.forEach(t => { gf[t.test_id] = { marks: t.marks_obtained ?? '', remarks: t.remarks ?? '' }; });
      setGradeStudentForm(gf);
    } catch { setGradeStudentTests([]); }
  };

  const saveStudentGrade = async (testId, maxMarks) => {
    const g = gradeStudentForm[testId] || {};
    const marks = g.marks !== '' ? parseInt(g.marks) : null;
    if (marks !== null && marks > maxMarks) { setMsg(`Marks cannot exceed ${maxMarks}`); return; }
    try {
      await API.post('/tests/grade', { test_id: testId, student_id: gradeStudentId, marks_obtained: marks, remarks: g.remarks || null });
      setMsg(`✓ Grade saved for ${gradeStudentSearch}`);
      const r = await API.get(`/tests/for-student/${gradeStudentId}`);
      setGradeStudentTests(r.data);
    } catch { setMsg('Failed to save grade'); }
  };
  const inp = (placeholder, value, onChange, type='text', extra={}) => (
    <input type={type} placeholder={placeholder} value={value} onChange={onChange} className="form-inp"
      style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', outline:'none', background:'#fff', ...extra }} />
  );

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      {/* Tab bar */}
      <div className="tutor-tabs" style={{ display:'flex', gap:0, marginBottom:24, background:'#f0f4fb', borderRadius:14, padding:5, maxWidth:'100%', overflowX:'auto', WebkitOverflowScrolling:'touch', border:'1px solid #e2e8f6', flexWrap:'nowrap' }}>
        {[['exampapers','① 📄 Upload Paper'],['create','② 📝 Schedule Test'],['list','📋 Test List'],['grades','📊 Grades'],['reschedule','🔄 Reschedule']].map(([t,label]) => (
          <button key={t} className="tab-btn" onClick={() => { setTab(t); if (t === 'create' || t === 'exampapers') API.get('/exams/my').then(r => setMyPapers(r.data)).catch(() => {}); }}
            style={{ padding:'9px 22px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:13, background: tab===t ? '#fff' : 'transparent', color: tab===t ? '#2554b8' : '#64748b', boxShadow: tab===t ? '0 2px 10px rgba(37,84,184,0.12)' : 'none', transition:'all 0.2s', position:'relative', whiteSpace:'nowrap', flexShrink:0 }}>
            {label}
            {t === 'reschedule' && rescheduleReqs.filter(r => r.status === 'pending').length > 0 && (
              <span style={{ position:'absolute', top:4, right:4, width:8, height:8, borderRadius:'50%', background:'#dc2626' }} />
            )}
          </button>
        ))}
      </div>

      {msg && <div style={{ padding:'11px 18px', borderRadius:10, fontSize:13, fontWeight:600, marginBottom:16, background: String(msg).startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', border:`1px solid ${String(msg).startsWith('✓') ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)'}`, color: String(msg).startsWith('✓') ? '#16a34a' : '#dc2626', cursor:'pointer' }} onClick={() => setMsg('')}>{msg}</div>}

      {tab === 'create' && (
        <div style={card()}>
          <div className="tutor-test-hero" style={{ display:'flex', gap:24, alignItems:'flex-start' }}>
            <div className="tutor-test-emoji" style={{ flexShrink:0, fontSize:100, lineHeight:1, opacity:0.5, userSelect:'none' }}>📝</div>
            <div style={{ flex:1, minWidth:0 }}>
              <h3 className="gradient-text" style={{ textAlign:'center', fontWeight:800, fontSize:20, marginBottom:22 }}>Test Creation Form</h3>
              <div className="tutor-test-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Test Name *</label>{inp('Enter test name', form.test_name, e=>setForm({...form,test_name:e.target.value}))}</div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Student *</label>
                  <select className="form-inp" value={form.student_id} onChange={e=>handleStudent(e.target.value)} style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', outline:'none', background:'#fff' }}>
                    <option value="">— Select Student —</option>
                    {students.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Subject *</label>
                  {studentSubs.length > 1 ? (
                    <select className="form-inp" value={form.subject} onChange={e=>handleSubject(e.target.value)} style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', outline:'none', background:'#fff' }}>
                      <option value="">— Select Subject —</option>
                      {studentSubs.map(e=><option key={e.subject} value={e.subject}>{e.subject}</option>)}
                    </select>
                  ) : inp('Auto-filled from student', form.subject, ()=>{}, 'text', { background:'#f8faff', cursor:'default' })}
                </div>
                <div><label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Class Name</label>{inp('Auto-filled', form.class_name, ()=>{}, 'text', { background:'#f8faff', cursor:'default' })}</div>
                <div><label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Topic</label>{inp('e.g. Algebra', form.topic, e=>setForm({...form,topic:e.target.value}))}</div>
                <div><label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Maximum Mark *</label>{inp('e.g. 100', form.max_marks, e=>setForm({...form,max_marks:e.target.value}),'number')}</div>
                <div><label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Test Date *</label>{inp('', form.test_date, e=>setForm({...form,test_date:e.target.value}),'date')}</div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Exam Paper *</label>
                  <select className="form-inp" value={form.exam_secret_code} onChange={e => setForm({...form, exam_secret_code: e.target.value})}
                    style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', outline:'none', background:'#fff' }}>
                    <option value="">— No exam paper —</option>
                    {myPapers.map(p => (
                      <option key={p.id} value={p.secret_code}>{p.title} ({p.secret_code})</option>
                    ))}
                  </select>
                </div>
                <div><label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Start Time</label><TimePicker12h className="form-inp" value={form.start_time} onChange={val=>setForm({...form,start_time:val})} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>End Time</label><TimePicker12h className="form-inp" value={form.end_time} onChange={val=>setForm({...form,end_time:val})} /></div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:20 }}>
                <button className="t-btn" onClick={createTest} style={{ padding:'12px 32px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:'0 4px 16px rgba(37,84,184,0.35)' }}>Create Test →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'list' && (
        <div style={card({ padding:0, overflow:'hidden' })}>
          <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9' }}><h3 style={{ fontWeight:800, fontSize:16, color:'#0f1f45' }}>Test List</h3></div>
          {tests.length === 0 ? <EmptyState icon="📝" text="No tests created yet" /> : (
            <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
                <thead><tr style={{ background:'#f8faff' }}>
                  {['Test Name','Student','Subject','Topic','Max','Class','Date','Start','End','Score','Remarks','Code','Actions'].map(h=><th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {tests.map((t,i)=>(
                    <tr key={t.id} className="t-tr t-row-anim" style={{ borderTop:'1px solid #f1f5f9', animationDelay:`${i*0.05}s` }}>
                      <td style={{ padding:'12px 16px', fontWeight:700, color:'#0f1f45', whiteSpace:'nowrap' }}>{t.test_name}</td>
                      <td style={{ padding:'12px 16px', color:'#475569', whiteSpace:'nowrap' }}>{t.student_name||'—'}</td>
                      <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>{t.subject}</td>
                      <td style={{ padding:'12px 16px', color:'#64748b', whiteSpace:'nowrap' }}>{t.topic||'—'}</td>
                      <td style={{ padding:'12px 16px', fontWeight:700, color:'#2554b8', whiteSpace:'nowrap' }}>{t.max_marks}</td>
                      <td style={{ padding:'12px 16px', color:'#64748b', whiteSpace:'nowrap' }}>{t.class_name||'—'}</td>
                      <td style={{ padding:'12px 16px', fontFamily:'monospace', fontSize:12, color:'#475569', whiteSpace:'nowrap' }}>{fmtDate(t.test_date)}</td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:'#475569', whiteSpace:'nowrap' }}>{t.start_time ? fmtTime(t.start_time) : '—'}</td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:'#475569', whiteSpace:'nowrap' }}>{t.end_time ? fmtTime(t.end_time) : '—'}</td>
                      <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>
                        {t.marks_obtained !== null && t.marks_obtained !== undefined ? (
                          <span style={{ fontWeight:800, color:'#16a34a', fontSize:13 }}>
                            {t.marks_obtained}<span style={{ color:'#94a3b8', fontWeight:600, fontSize:11 }}> / {t.max_marks}</span>
                            {t.graded_count > 1 && <span style={{ marginLeft:4, fontSize:10, color:'#94a3b8' }}>(+{t.graded_count - 1})</span>}
                          </span>
                        ) : <span style={{ color:'#94a3b8', fontSize:12 }}>—</span>}
                      </td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:'#64748b', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.remarks || ''}>{t.remarks || '—'}</td>
                      <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}><span style={{ fontFamily:'monospace', fontSize:12, color:'#7c3aed', background:'#f5f3ff', padding:'3px 8px', borderRadius:6 }}>{t.exam_secret_code||'—'}</span></td>
                      <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>
                        <button className="t-btn" onClick={()=>openGrades(t)} style={{ padding:'6px 16px', borderRadius:8, border:'1.5px solid #2554b8', background:'transparent', color:'#2554b8', fontWeight:700, cursor:'pointer', fontSize:12, marginRight:6 }}>Grades</button>
                        {t.deletable !== false ? (
                          <button className="t-btn" onClick={()=>deleteTest(t)} title="Delete this scheduled test" style={{ padding:'6px 12px', borderRadius:8, border:'1.5px solid #dc2626', background:'transparent', color:'#dc2626', fontWeight:700, cursor:'pointer', fontSize:12 }}>🗑 Delete</button>
                        ) : (
                          <span title="Marks recorded or exam attended — can't delete" style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>🔒 Locked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'reschedule' && (
        <div>
          {rescheduleReviewMsg && (
            <div style={{ marginBottom:16, padding:'12px 18px', borderRadius:10, background: rescheduleReviewMsg.startsWith('✓') ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)', color: rescheduleReviewMsg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight:700, fontSize:13 }}>{rescheduleReviewMsg}</div>
          )}
          {rescheduleReqs.length === 0 ? (
            <div style={card({ textAlign:'center', padding:52 })}>
              <div style={{ fontSize:48, marginBottom:12 }}>🔄</div>
              <p style={{ fontWeight:600, color:'#64748b' }}>No reschedule requests from students</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {rescheduleReqs.map(r => (
                <div key={r.id} style={card({ padding:20, border: r.status==='pending' ? '1px solid rgba(217,119,6,0.3)' : '1px solid #e8edf8' })}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
                        <span style={{ fontWeight:800, fontSize:15, color:'#0f1f45' }}>{r.test_name}</span>
                        <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background: r.status==='pending'?'rgba(217,119,6,0.12)':r.status==='approved'?'rgba(22,163,74,0.12)':'rgba(220,38,38,0.12)', color: r.status==='pending'?'#d97706':r.status==='approved'?'#16a34a':'#dc2626' }}>{r.status}</span>
                      </div>
                      <div style={{ fontSize:13, color:'#475569', marginBottom:4 }}>👤 <strong>{r.student_name}</strong> &nbsp;·&nbsp; 📚 {r.subject} &nbsp;·&nbsp; 📅 {r.test_date||'—'}</div>
                      <div style={{ fontSize:13, color:'#334155', marginTop:8, padding:'10px 14px', background:'#f8faff', borderRadius:8, border:'1px solid #e8edf8' }}>
                        <strong>Reason:</strong> {r.reason}
                      </div>
                      {r.tutor_response && (
                        <div style={{ fontSize:12, color:'#64748b', marginTop:6, fontStyle:'italic' }}>Response: {r.tutor_response}</div>
                      )}
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:6 }}>Requested: {r.requested_at}</div>
                    </div>
                    {r.status === 'pending' && (
                      <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                        <button onClick={() => reviewReschedule(r.id, 'approved', 'Approved')}
                          style={{ padding:'8px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                          ✓ Approve
                        </button>
                        <button onClick={() => reviewReschedule(r.id, 'rejected', 'Rejected')}
                          style={{ padding:'8px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                          ✗ Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'grades' && (
        <div>
          {/* ── Mode A: Test-centric (arrived via "Grades" button in Test List) ── */}
          {gradeTest && (
            <div style={card({ padding:0, overflow:'hidden' })}>
              <div style={{ padding:'16px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                <div>
                  <h3 style={{ fontWeight:800, fontSize:16, color:'#0f1f45', margin:0 }}>
                    📝 {gradeTest.test_name}
                    <span style={{ marginLeft:10, fontWeight:500, fontSize:13, color:'#64748b' }}>{gradeTest.subject} · Max: {gradeTest.max_marks}</span>
                  </h3>
                  <p style={{ margin:'4px 0 0', fontSize:12, color:'#94a3b8' }}>{fmtDate(gradeTest.test_date)}{gradeTest.class_name ? ` · Class: ${gradeTest.class_name}` : ''}</p>
                </div>
                <button onClick={() => { setGradeTest(null); setGradeEntries([]); }}
                  style={{ padding:'6px 14px', borderRadius:8, border:'1px solid #e2e8f6', background:'#f8faff', color:'#64748b', fontWeight:600, cursor:'pointer', fontSize:12 }}>
                  ✕ Clear
                </button>
              </div>
              {gradeEntries.length === 0
                ? <EmptyState icon="👥" text="No students linked to this test" />
                : (
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'#f8faff' }}>
                      {['Student',`Marks / ${gradeTest.max_marks}`,'Remarks',''].map(h=>(
                        <th key={h} style={{ padding:'12px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7 }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {gradeEntries.map(e => {
                        const g = gradeForm[e.student_id] || { marks: e.marks_obtained ?? '', remarks: e.remarks ?? '' };
                        const saved = e.marks_obtained !== null && e.marks_obtained !== undefined;
                        return (
                          <tr key={e.student_id} className="t-tr" style={{ borderTop:'1px solid #f1f5f9' }}>
                            <td style={{ padding:'12px 20px', fontWeight:700, color:'#0f1f45' }}>{e.student_name}</td>
                            <td style={{ padding:'12px 20px' }}>
                              <input type="number" min={0} max={gradeTest.max_marks} value={g.marks}
                                onChange={ev => setGradeForm(f => ({ ...f, [e.student_id]: { ...f[e.student_id], marks: ev.target.value } }))}
                                className="form-inp" style={{ width:90, border:'1.5px solid #e2e8f6', borderRadius:8, padding:'7px 10px', fontSize:14, outline:'none', textAlign:'center' }} />
                            </td>
                            <td style={{ padding:'12px 20px' }}>
                              <input value={g.remarks} onChange={ev => setGradeForm(f => ({ ...f, [e.student_id]: { ...f[e.student_id], remarks: ev.target.value } }))}
                                placeholder="Remarks…" className="form-inp"
                                style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:8, padding:'7px 10px', fontSize:14, outline:'none' }} />
                            </td>
                            <td style={{ padding:'12px 20px' }}>
                              <button className="t-btn" onClick={() => saveGrade(e.student_id, e.student_name)}
                                style={{ padding:'7px 18px', borderRadius:8, border:'none', background: saved ? 'linear-gradient(135deg,#16a34a,#22c55e)' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
                                {saved ? '✓ Update' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              }
            </div>
          )}

          {/* ── Mode B: Student-centric (search student → see all their tests) ── */}
          <div style={card({ marginTop: gradeTest ? 20 : 0 })}>
            <h3 style={{ fontWeight:800, fontSize:15, color:'#0f1f45', marginBottom:16 }}>🔍 Search Student — View All Tests</h3>
            <div style={{ position:'relative', maxWidth:340, marginBottom:20 }}>
              <input className="form-inp" placeholder="Type student name…" value={gradeStudentSearch} autoComplete="off"
                onChange={e => { setGradeStudentSearch(e.target.value); setShowGradeStuList(true); if (!e.target.value) { setGradeStudentId(''); setGradeStudentTests([]); } }}
                onFocus={() => setShowGradeStuList(true)}
                onBlur={() => setTimeout(() => setShowGradeStuList(false), 180)}
                style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', outline:'none', background:'#fff' }} />
              {showGradeStuList && gradeStudentSearch && (() => {
                const matches = students.filter(s => s.name.toLowerCase().includes(gradeStudentSearch.toLowerCase()));
                if (!matches.length) return null;
                return (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #dbe1f0', borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:200, maxHeight:220, overflowY:'auto' }}>
                    {matches.map(s => (
                      <div key={s.id} onMouseDown={() => selectGradeStudent(s)}
                        style={{ padding:'10px 16px', cursor:'pointer', borderBottom:'1px solid #f1f5f9', fontWeight:600, color:'#0f1f45' }}
                        onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
                        onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                        {s.name}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {!gradeStudentId
              ? <p style={{ color:'#94a3b8', fontSize:13 }}>Select a student above to view and enter marks for all their tests.</p>
              : gradeStudentTests.length === 0
              ? <EmptyState icon="📝" text="No tests assigned to this student yet" />
              : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'#f8faff' }}>
                      {['Test Name','Subject','Date','Max','Marks Obtained','Remarks',''].map(h=>(
                        <th key={h} style={{ padding:'11px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7 }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {gradeStudentTests.map((t,i) => {
                        const g = gradeStudentForm[t.test_id] || { marks: t.marks_obtained ?? '', remarks: t.remarks ?? '' };
                        const saved = t.marks_obtained !== null && t.marks_obtained !== undefined;
                        return (
                          <tr key={t.test_id} className="t-tr t-row-anim" style={{ borderTop:'1px solid #f1f5f9', animationDelay:`${i*0.04}s` }}>
                            <td style={{ padding:'11px 14px', fontWeight:700, color:'#0f1f45' }}>{t.test_name}</td>
                            <td style={{ padding:'11px 14px', color:'#475569' }}>{t.subject}</td>
                            <td style={{ padding:'11px 14px', fontFamily:'monospace', fontSize:12, color:'#475569' }}>{fmtDate(t.test_date)}</td>
                            <td style={{ padding:'11px 14px', fontWeight:700, color:'#2554b8', textAlign:'center' }}>{t.max_marks}</td>
                            <td style={{ padding:'11px 14px' }}>
                              <input type="number" min={0} max={t.max_marks} value={g.marks}
                                onChange={ev => setGradeStudentForm(f => ({ ...f, [t.test_id]: { ...f[t.test_id], marks: ev.target.value } }))}
                                className="form-inp" style={{ width:80, border:'1.5px solid #e2e8f6', borderRadius:8, padding:'7px 10px', fontSize:14, outline:'none', textAlign:'center' }} />
                            </td>
                            <td style={{ padding:'11px 14px' }}>
                              <input value={g.remarks} onChange={ev => setGradeStudentForm(f => ({ ...f, [t.test_id]: { ...f[t.test_id], remarks: ev.target.value } }))}
                                placeholder="Remarks…" className="form-inp"
                                style={{ width:'100%', minWidth:130, border:'1.5px solid #e2e8f6', borderRadius:8, padding:'7px 10px', fontSize:14, outline:'none' }} />
                            </td>
                            <td style={{ padding:'11px 14px' }}>
                              <button className="t-btn" onClick={() => saveStudentGrade(t.test_id, t.max_marks)}
                                style={{ padding:'6px 16px', borderRadius:8, border:'none', background: saved ? 'linear-gradient(135deg,#16a34a,#22c55e)' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
                                {saved ? '✓ Update' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </div>
      )}

      {tab === 'exampapers' && (
        <ExamPapersTab
          onProceedToTest={(paper) => {
            // Guided flow: after uploading a question paper, jump straight to
            // the Create Test form with this paper pre-selected.
            API.get('/exams/my').then(r => setMyPapers(r.data)).catch(() => {});
            setForm(f => ({ ...f, exam_secret_code: paper?.secret_code || '' }));
            setTab('create');
            setMsg('✓ Question paper uploaded. Now schedule the test below.');
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   EXAM PAPERS TAB (inside TestsAndGrades)
═══════════════════════════════════════════════════ */

function ExamPapersTab({ onProceedToTest }) {
  const [papers, setPapers]           = useState([]);
  const [form, setForm]               = useState({ title:'', subject:'', secret_code:'', duration_minutes:60 });
  const [pdfFile, setPdfFile]         = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [msg, setMsg]                 = useState('');
  const [justCreated, setJustCreated] = useState(null); // paper object after a successful upload
  const [viewSessions, setViewSessions] = useState(null); // { paper, sessions }
  const fileRef                       = useRef(null);

  const load = useCallback(() => {
    API.get('/exams/my').then(r => setPapers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const autoCode = () => {
    const code = 'EXAM-' + Math.random().toString(36).substr(2, 8).toUpperCase();
    setForm(f => ({ ...f, secret_code: code }));
  };

  const handleCreate = async () => {
    if (!form.title || !form.secret_code || !pdfFile) { setMsg('Fill title, secret code and select a PDF.'); return; }
    setUploading(true); setMsg('Saving exam paper...'); setJustCreated(null);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('subject', form.subject || '');
      fd.append('secret_code', form.secret_code);
      fd.append('duration_minutes', String(parseInt(form.duration_minutes) || 60));
      fd.append('pdf_file', pdfFile);
      const res = await API.post('/exams/', fd);
      setMsg('');
      // Capture the created paper so we can offer "Next: create the test".
      setJustCreated(res.data || { title: form.title, secret_code: form.secret_code, subject: form.subject });
      setForm({ title:'', subject:'', secret_code:'', duration_minutes:60 });
      setPdfFile(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) { setMsg(e.response?.data?.detail || e.message || 'Failed to create exam paper.'); }
    finally { setUploading(false); }
  };

  const viewPdf = async (paper) => {
    const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    try {
      const r = await API.get(`/exams/${paper.id}/pdf-access`);
      const pt = r.data?.pdf_token;
      if (!pt) throw new Error('no pdf token');
      window.open(`${base}/exams/${paper.id}/pdf?pdf_token=${encodeURIComponent(pt)}`, '_blank');
    } catch (_) {
      alert('Could not open PDF.');
    }
  };

  const toggleActive = async (id) => {
    await API.patch(`/exams/${id}/toggle`).catch(() => {});
    load();
  };

  const openSessions = async (paper) => {
    try {
      const r = await API.get(`/exams/${paper.id}/sessions`);
      setViewSessions({ paper, sessions: r.data });
    } catch (e) { setMsg('Failed to load sessions.'); }
  };

  const [sessSearch, setSessSearch]     = useState({ name:'', from:'', to:'' });

  if (viewSessions) {
    const { paper, sessions } = viewSessions;
    const filtered = sessions.filter(s => {
      const nameOk = !sessSearch.name || s.student_name.toLowerCase().includes(sessSearch.name.toLowerCase());
      const fromOk = !sessSearch.from || new Date(s.started_at) >= new Date(sessSearch.from);
      const toOk   = !sessSearch.to   || new Date(s.started_at) <= new Date(sessSearch.to + 'T23:59:59');
      return nameOk && fromOk && toOk;
    });
    return (
      <div>
        <button onClick={() => { setViewSessions(null); setSessSearch({ name:'', from:'', to:'' }); }} style={{ marginBottom:16, padding:'8px 18px', borderRadius:10, border:'none', background:'#f1f5f9', color:'#2554b8', fontWeight:700, cursor:'pointer', fontSize:13 }}>
          ← Back to Papers
        </button>
        <div style={{ fontWeight:800, fontSize:17, color:'#0f1f45', marginBottom:4 }}>{paper.title}</div>
        <div style={{ fontSize:12, color:'#64748b', marginBottom:14 }}>Code: <strong>{paper.secret_code}</strong> · {sessions.length} student session{sessions.length !== 1 ? 's' : ''}</div>
        {/* Search / filter bar */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:18, padding:'12px 14px', background:'#f8faff', borderRadius:12, border:'1px solid #e2e8f0' }}>
          <input value={sessSearch.name} onChange={e => setSessSearch(f => ({ ...f, name:e.target.value }))}
            placeholder="Search by student name…"
            style={{ flex:'1 1 160px', padding:'8px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, outline:'none' }} />
          <input type="date" value={sessSearch.from} onChange={e => setSessSearch(f => ({ ...f, from:e.target.value }))}
            style={{ flex:'0 0 140px', padding:'8px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, outline:'none' }} />
          <input type="date" value={sessSearch.to} onChange={e => setSessSearch(f => ({ ...f, to:e.target.value }))}
            style={{ flex:'0 0 140px', padding:'8px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, outline:'none' }} />
          {(sessSearch.name || sessSearch.from || sessSearch.to) && (
            <button onClick={() => setSessSearch({ name:'', from:'', to:'' })} style={{ padding:'8px 14px', borderRadius:8, border:'none', background:'#f0f4ff', color:'#2554b8', fontWeight:700, fontSize:12, cursor:'pointer' }}>Clear</button>
          )}
        </div>
        {filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:14 }}>No sessions match the search criteria.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {filtered.map(s => (
              <div key={s.id} style={{ background:'#fff', borderRadius:14, padding:16, border:'1px solid #e2e8f0', boxShadow:'0 1px 6px rgba(0,0,0,0.05)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:8 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#0f1f45' }}>{s.student_name}</div>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {s.flag_count > 0 && <span style={{ padding:'3px 10px', borderRadius:20, background:'rgba(220,38,38,0.1)', color:'#dc2626', fontSize:11, fontWeight:700 }}>⚠️ {s.flag_count} flag{s.flag_count !== 1 ? 's' : ''}</span>}
                    <span style={{ padding:'3px 10px', borderRadius:20, background: s.completed_at ? 'rgba(22,163,74,0.1)' : 'rgba(251,191,36,0.15)', color: s.completed_at ? '#16a34a' : '#d97706', fontSize:11, fontWeight:700 }}>
                      {s.completed_at ? '✓ Completed' : '⏳ In progress'}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:8 }}>Started: {s.started_at?.slice(0,19).replace('T',' ')}</div>
                {s.flags.length > 0 && (
                  <div style={{ background:'#fef2f2', borderRadius:8, padding:10, maxHeight:160, overflow:'auto' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#dc2626', marginBottom:6 }}>PROCTORING FLAGS</div>
                    {s.flags.map((f, i) => (
                      <div key={i} style={{ fontSize:11, color:'#7f1d1d', borderBottom:'1px solid #fecaca', paddingBottom:4, marginBottom:4 }}>
                        <span style={{ fontWeight:600 }}>{f.reason}</span> <span style={{ color:'#94a3b8', fontSize:10 }}>{f.ts?.slice(0,19)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Create form */}
      <div style={{ background:'#fff', borderRadius:16, padding:24, marginBottom:24, border:'1px solid #e2e8f0', boxShadow:'0 1px 8px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontWeight:800, fontSize:16, color:'#0f1f45', marginBottom:18 }}>📄 Upload New Exam Paper</h3>
        {msg && <div style={{ padding:'10px 14px', borderRadius:8, fontSize:13, fontWeight:600, marginBottom:14, background: msg.startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', color: msg.startsWith('✓') ? '#16a34a' : '#dc2626' }} onClick={() => setMsg('')}>{msg}</div>}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>EXAM TITLE *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title:e.target.value }))} placeholder="e.g. Chapter 5 Test" style={{ width:'100%', padding:'10px 14px', borderRadius:10, border:'1.5px solid #e2e8f6', fontSize:14, outline:'none', boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>SUBJECT</label>
            <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject:e.target.value }))} placeholder="e.g. Mathematics" style={{ width:'100%', padding:'10px 14px', borderRadius:10, border:'1.5px solid #e2e8f6', fontSize:14, outline:'none', boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>SECRET CODE *</label>
            <div style={{ display:'flex', gap:8 }}>
              <input value={form.secret_code} onChange={e => setForm(f => ({ ...f, secret_code:e.target.value.toUpperCase() }))} placeholder="EXAM-XXXXXXXX" style={{ flex:1, padding:'10px 14px', borderRadius:10, border:'1.5px solid #e2e8f6', fontSize:14, outline:'none', fontFamily:'monospace', letterSpacing:1 }} />
              <button onClick={autoCode} style={{ padding:'10px 14px', borderRadius:10, border:'none', background:'#f0f4ff', color:'#2554b8', fontWeight:700, fontSize:12, cursor:'pointer', whiteSpace:'nowrap' }}>Auto</button>
            </div>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>DURATION (minutes) *</label>
            <input type="number" min="10" max="300" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes:e.target.value }))} style={{ width:'100%', padding:'10px 14px', borderRadius:10, border:'1.5px solid #e2e8f6', fontSize:14, outline:'none', boxSizing:'border-box' }} />
          </div>
        </div>
        <div style={{ marginTop:14 }}>
          <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>QUESTION PAPER (PDF) *</label>
          <input ref={fileRef} type="file" accept="application/pdf" onChange={e => setPdfFile(e.target.files[0] || null)}
            style={{ display:'block', width:'100%', padding:'10px', borderRadius:10, border:'1.5px dashed #cbd5e1', background:'#f8faff', cursor:'pointer', fontSize:13 }} />
          {pdfFile && <p style={{ fontSize:12, color:'#16a34a', marginTop:6 }}>✓ {pdfFile.name} ({(pdfFile.size/1024).toFixed(0)} KB)</p>}
        </div>
        <button onClick={handleCreate} disabled={uploading}
          style={{ marginTop:18, padding:'12px 32px', borderRadius:12, border:'none', background: uploading ? '#94a3b8' : 'linear-gradient(135deg,#7c3aed,#a855f7)', color:'#fff', fontWeight:800, fontSize:14, cursor: uploading ? 'not-allowed' : 'pointer', boxShadow:'0 4px 14px rgba(124,58,237,0.35)' }}>
          {uploading ? '⏳ Uploading...' : '📤 Upload Question Paper'}
        </button>

        {/* Guided next step — after the paper is uploaded, go schedule the test */}
        {justCreated && (
          <div style={{ marginTop:18, padding:'16px 18px', borderRadius:14, background:'linear-gradient(135deg,#f0fdf4,#dcfce7)', border:'1px solid #86efac' }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#15803d', marginBottom:4 }}>✓ Question paper uploaded</div>
            <div style={{ fontSize:12.5, color:'#166534', marginBottom:12, lineHeight:1.5 }}>
              <strong>{justCreated.title}</strong> · Code <span style={{ fontFamily:'monospace', fontWeight:700 }}>{justCreated.secret_code}</span>.<br/>
              Next step: schedule the test so students can attend and you can record marks.
            </div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button onClick={() => onProceedToTest && onProceedToTest(justCreated)}
                style={{ padding:'11px 22px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:800, fontSize:13, cursor:'pointer', boxShadow:'0 4px 12px rgba(22,163,74,0.3)' }}>
                📅 Schedule & Create Test →
              </button>
              <button onClick={() => setJustCreated(null)}
                style={{ padding:'11px 18px', borderRadius:10, border:'1.5px solid #cbd5e1', background:'#fff', color:'#64748b', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                Upload another paper
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Papers list */}
      <div style={{ fontWeight:800, fontSize:15, color:'#0f1f45', marginBottom:14 }}>Your Exam Papers ({papers.length})</div>
      {papers.length === 0 ? (
        <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:14 }}>No exam papers uploaded yet.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {papers.map(p => (
            <div key={p.id} style={{ background:'#fff', borderRadius:14, padding:'14px 18px', border:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
              <div>
                <div style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>{p.title}</div>
                <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
                  {p.subject && <span style={{ marginRight:8 }}>{p.subject} · </span>}
                  Code: <strong style={{ fontFamily:'monospace', color:'#7c3aed' }}>{p.secret_code}</strong> · {p.duration_minutes} min
                </div>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                  {p.session_count} student{p.session_count !== 1 ? 's' : ''} · {p.total_flags} flag{p.total_flags !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background: p.is_active ? 'rgba(22,163,74,0.1)' : 'rgba(100,116,139,0.1)', color: p.is_active ? '#16a34a' : '#64748b' }}>
                  {p.is_active ? '● Active' : '○ Inactive'}
                </span>
                {p.has_pdf && <button onClick={() => viewPdf(p)} style={{ padding:'6px 12px', borderRadius:8, border:'none', background:'#f0f4ff', color:'#7c3aed', fontWeight:700, fontSize:12, cursor:'pointer' }}>View PDF</button>}
                <button onClick={() => openSessions(p)} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#f0f4ff', color:'#2554b8', fontWeight:700, fontSize:12, cursor:'pointer' }}>Sessions</button>
                <button onClick={() => toggleActive(p.id)} style={{ padding:'6px 12px', borderRadius:8, border:'none', background: p.is_active ? '#fef2f2' : '#f0fdf4', color: p.is_active ? '#dc2626' : '#16a34a', fontWeight:700, fontSize:12, cursor:'pointer' }}>
                  {p.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   LEAVE REQUEST
═══════════════════════════════════════════════════ */
function LeaveRequest() {
  const [leaveType, setLeaveType] = useState('');
  const [form, setForm] = useState({ start_date:'', end_date:'', reason:'' });
  const [myLeaves, setMyLeaves] = useState([]);
  const [msg, setMsg] = useState('');
  const [monthUsage, setMonthUsage] = useState({ used:0, limit:2 });
  const [classesOnDates, setClassesOnDates] = useState([]);  // sessions on selected dates
  const [selectedScheduleIds, setSelectedScheduleIds] = useState([]);  // which to cancel
  const [loadingClasses, setLoadingClasses] = useState(false);

  const leaveTypes = [
    { id:'Sick Leave',     icon:'🤒', label:'Sick Leave',     color:'#dc2626', bg:'#fef2f2' },
    { id:'Vacation Leave', icon:'✈️', label:'Vacation Leave', color:'#2554b8', bg:'#eff6ff' },
    { id:'Personal Leave', icon:'👤', label:'Personal Leave', color:'#7c3aed', bg:'#f5f3ff' },
  ];

  const now = new Date();
  const loadMonthUsage = useCallback((year, month) => {
    const y = year || now.getFullYear();
    const m = month || now.getMonth() + 1;
    API.get(`/leave/month-usage?year=${y}&month=${m}`)
      .then(r => setMonthUsage(r.data)).catch(() => {});
  }, []);
  const loadLeaves = useCallback(() => { API.get('/leave/my').then(r=>setMyLeaves(r.data)).catch(()=>{}); }, []);
  useEffect(() => { loadLeaves(); loadMonthUsage(); }, [loadLeaves, loadMonthUsage]);

  // When dates change, fetch classes on those dates
  const fetchClassesOnDates = async (start, end) => {
    if (!start) return;
    const dates = [];
    let cur = new Date(start);
    const endD = end ? new Date(end) : new Date(start);
    while (cur <= endD) { dates.push(cur.toISOString().slice(0,10)); cur.setDate(cur.getDate()+1); }
    setLoadingClasses(true);
    try {
      const r = await API.post('/leave/schedules-on-dates', dates);
      setClassesOnDates(r.data);
      setSelectedScheduleIds(r.data.map(s => s.id));  // select all by default
    } catch { setClassesOnDates([]); }
    setLoadingClasses(false);
  };

  const handleDateChange = (key, val) => {
    const updated = { ...form, [key]: val };
    setForm(updated);
    if (updated.start_date) {
      // Update quota check for the month of the selected start date
      if (key === 'start_date' && val) {
        const d = new Date(val);
        loadMonthUsage(d.getFullYear(), d.getMonth() + 1);
      }
      // Validate span
      if (updated.end_date && updated.end_date < updated.start_date) {
        setMsg('End date cannot be before start date'); return;
      }
      if (updated.start_date && updated.end_date) {
        const span = (new Date(updated.end_date) - new Date(updated.start_date)) / 86400000 + 1;
        if (span > 2) { setMsg('You can select at most 2 days per leave request'); return; }
      }
      setMsg('');
      fetchClassesOnDates(updated.start_date, updated.end_date || updated.start_date);
    }
  };

  const toggleSchedule = (id) => {
    setSelectedScheduleIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };

  const submit = async () => {
    if (!leaveType) { setMsg('Select a leave type'); return; }
    if (!form.start_date) { setMsg('Select a start date'); return; }
    const endDate = form.end_date || form.start_date;
    const span = (new Date(endDate) - new Date(form.start_date)) / 86400000 + 1;
    if (span > 2) { setMsg('You can select at most 2 days per leave request'); return; }
    if (monthUsage.used >= monthUsage.limit) { setMsg('Monthly leave quota exhausted'); return; }
    try {
      await API.post('/leave/', { leave_type:leaveType, start_date:form.start_date, end_date:endDate, reason:form.reason||null, schedule_ids:selectedScheduleIds });
      setMsg('✓ Leave applied! Selected classes have been cancelled.');
      setLeaveType(''); setForm({start_date:'',end_date:'',reason:''}); setClassesOnDates([]); setSelectedScheduleIds([]);
      loadLeaves(); loadMonthUsage();
    } catch (e) { setMsg(e.response?.data?.detail||'Failed'); }
  };

  const statusStyle = (s) => ({ approved:'rgba(22,163,74,0.1)|#16a34a|rgba(22,163,74,0.2)', rejected:'rgba(220,38,38,0.1)|#dc2626|rgba(220,38,38,0.2)', pending:'rgba(217,119,6,0.1)|#d97706|rgba(217,119,6,0.2)' }[s]||'rgba(100,116,139,0.1)|#64748b|rgba(100,116,139,0.2)').split('|');

  const quotaExhausted = monthUsage.used >= monthUsage.limit;
  const remaining = monthUsage.limit - monthUsage.used;

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      <div style={{ maxWidth:740, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ width:70, height:70, borderRadius:'50%', background:'linear-gradient(135deg,#2554b8,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, margin:'0 auto 14px', boxShadow:'0 8px 24px rgba(37,84,184,0.3)' }}>📄</div>
          <h2 style={{ fontWeight:800, fontSize:24, color:'#0f1f45', marginBottom:6 }}>Leave Request</h2>
          <p style={{ color:'#64748b', fontSize:14 }}>2 leaves allowed per month. Classes on leave days are cancelled automatically.</p>
        </div>

        {/* Monthly quota banner */}
        <div style={{ borderRadius:14, padding:'14px 22px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, background: quotaExhausted ? 'rgba(220,38,38,0.08)' : 'linear-gradient(135deg,#2554b8,#3b82f6)', border: quotaExhausted ? '1.5px solid rgba(220,38,38,0.25)' : 'none' }}>
          <div style={{ color: quotaExhausted ? '#dc2626' : '#fff', fontWeight:700, fontSize:14 }}>
            {quotaExhausted
            ? `🚫 Leave quota exhausted for ${monthUsage.month ? new Date(monthUsage.year, monthUsage.month-1).toLocaleString('default',{month:'long',year:'numeric'}) : 'this month'} — select a date in another month`
            : `📅 Leave quota: ${monthUsage.used} / ${monthUsage.limit} days used${monthUsage.month ? ` (${new Date(monthUsage.year, monthUsage.month-1).toLocaleString('default',{month:'long',year:'numeric'})})` : ' this month'}`
          }
          </div>
          <div style={{ display:'flex', gap:6 }}>
            {[...Array(monthUsage.limit)].map((_,i) => (
              <div key={i} style={{ width:32, height:32, borderRadius:8, background: i < monthUsage.used ? 'rgba(220,38,38,0.5)' : 'rgba(255,255,255,0.25)', border:'2px solid rgba(255,255,255,0.4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>
                {i < monthUsage.used ? '✕' : '○'}
              </div>
            ))}
          </div>
          {!quotaExhausted && <span style={{ color:'rgba(255,255,255,0.85)', fontSize:12 }}>{remaining} day(s) remaining</span>}
        </div>

        {msg && <div style={{ padding:'12px 18px', borderRadius:10, fontSize:13, fontWeight:600, marginBottom:16, background: String(msg).startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', border:`1px solid ${String(msg).startsWith('✓') ? 'rgba(22,163,74,0.25)' : 'rgba(220,38,38,0.25)'}`, color: String(msg).startsWith('✓') ? '#16a34a' : '#dc2626', cursor:'pointer' }} onClick={()=>setMsg('')}>{msg}</div>}

        <>
            {/* Leave type */}
            <div style={{ ...card(), marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, display:'block', marginBottom:14 }}>Leave Type</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                {leaveTypes.map(lt => (
                  <div key={lt.id} className="leave-card" onClick={()=>setLeaveType(lt.id)}
                    style={{ border:`2px solid ${leaveType===lt.id ? lt.color : '#e2e8f6'}`, borderRadius:14, padding:'18px 14px', textAlign:'center', background: leaveType===lt.id ? lt.bg : '#fff', boxShadow: leaveType===lt.id ? `0 6px 20px ${lt.color}22` : 'none' }}>
                    <div style={{ fontSize:30, marginBottom:8 }}>{lt.icon}</div>
                    <div style={{ fontSize:13, fontWeight:700, color: leaveType===lt.id ? lt.color : '#0f1f45' }}>{lt.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dates + reason */}
            <div style={{ ...card(), marginBottom:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
                {[['Start Date (required)','start_date'],['End Date (optional, max +1 day)','end_date']].map(([label,key])=>(
                  <div key={key}>
                    <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>{label}</label>
                    <input type="date" value={form[key]} min={new Date().toISOString().slice(0,10)}
                      onChange={e=>handleDateChange(key, e.target.value)} className="form-inp"
                      style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', outline:'none' }} />
                  </div>
                ))}
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>Reason for Leave</label>
                <textarea value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} rows={3}
                  placeholder="Please provide a reason for your leave request…" className="form-inp"
                  style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'vertical', outline:'none' }} />
              </div>
            </div>

            {/* Classes on selected dates */}
            {form.start_date && (
              <div style={{ ...card(), marginBottom:16 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
                  <h4 style={{ fontWeight:800, color:'#0f1f45', fontSize:14, margin:0 }}>📚 Classes on Selected Date(s)</h4>
                  {classesOnDates.length > 0 && (
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="t-btn" onClick={() => setSelectedScheduleIds(classesOnDates.map(s=>s.id))}
                        style={{ padding:'5px 12px', borderRadius:7, border:'1.5px solid #2554b8', background:'#f0f4ff', color:'#2554b8', fontWeight:700, fontSize:11, cursor:'pointer' }}>Select All</button>
                      <button className="t-btn" onClick={() => setSelectedScheduleIds([])}
                        style={{ padding:'5px 12px', borderRadius:7, border:'1.5px solid #e2e8f6', background:'#f8faff', color:'#64748b', fontWeight:700, fontSize:11, cursor:'pointer' }}>Deselect All</button>
                    </div>
                  )}
                </div>
                {loadingClasses ? (
                  <p style={{ color:'#64748b', fontSize:13 }}>Loading classes…</p>
                ) : classesOnDates.length === 0 ? (
                  <p style={{ color:'#94a3b8', fontSize:13 }}>No scheduled classes on the selected date(s). Leave will be recorded with no class cancellations.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {classesOnDates.map(s => {
                      const checked = selectedScheduleIds.includes(s.id);
                      return (
                        <div key={s.id} onClick={() => toggleSchedule(s.id)}
                          style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:12, border:`2px solid ${checked ? '#2554b8' : '#e2e8f6'}`, background: checked ? '#f0f4ff' : '#fafbff', cursor:'pointer', transition:'all 0.15s' }}>
                          <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${checked ? '#2554b8' : '#cbd5e1'}`, background: checked ? '#2554b8' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:'#fff', flexShrink:0 }}>
                            {checked ? '✓' : ''}
                          </div>
                          <div style={{ flex:1 }}>
                            <span style={{ fontWeight:700, color:'#0f1f45', fontSize:13 }}>{s.subject}</span>
                            <span style={{ color:'#64748b', fontSize:12, marginLeft:8 }}>with {s.student_name}</span>
                          </div>
                          <div style={{ fontSize:12, color:'#475569', fontFamily:'monospace' }}>{fmtDate(s.date)} · {fmtTime(s.start_time, s.date)}–{fmtTime(s.end_time, s.date)}</div>
                          {checked && <span style={{ fontSize:11, fontWeight:700, color:'#dc2626', background:'rgba(220,38,38,0.1)', padding:'2px 8px', borderRadius:20, border:'1px solid rgba(220,38,38,0.2)' }}>Will cancel</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <button className="t-btn" onClick={submit}
              style={{ width:'100%', padding:'13px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:800, cursor:'pointer', fontSize:15, boxShadow:'0 6px 20px rgba(37,84,184,0.35)', marginBottom:24 }}>
              Submit Leave Request →
            </button>
        </>

        {/* Leave history */}
        {myLeaves.length > 0 && (
          <div style={{ ...card({ padding:0, overflow:'hidden' }) }}>
            <div style={{ padding:'16px 22px', borderBottom:'1px solid #f1f5f9' }}><h4 style={{ fontWeight:800, color:'#0f1f45' }}>My Leave History</h4></div>
            <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:480 }}>
              <thead><tr style={{ background:'#f8faff' }}>
                {['Type','From','To','Classes Cancelled','Status'].map(h=><th key={h} style={{ padding:'11px 18px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {myLeaves.map(l=>{const [bg,color,border]=statusStyle(l.status); return (
                  <tr key={l.id} className="t-tr" style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'11px 18px', fontWeight:600, whiteSpace:'nowrap' }}>{l.leave_type}</td>
                    <td style={{ padding:'11px 18px', fontFamily:'monospace', fontSize:12, whiteSpace:'nowrap' }}>{fmtDate(l.start_date)}</td>
                    <td style={{ padding:'11px 18px', fontFamily:'monospace', fontSize:12, whiteSpace:'nowrap' }}>{fmtDate(l.end_date)}</td>
                    <td style={{ padding:'11px 18px', textAlign:'center', fontWeight:700, color: l.cancelled_count > 0 ? '#dc2626' : '#64748b', whiteSpace:'nowrap' }}>{l.cancelled_count || 0}</td>
                    <td style={{ padding:'11px 18px', whiteSpace:'nowrap' }}><span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, background:bg, color, border:`1px solid ${border}` }}>{l.status}</span></td>
                  </tr>
                );})}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   HOMEWORK STATUS
═══════════════════════════════════════════════════ */
function HomeworkStatus() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    API.get('/class/logs').then(r => {
      const withHw = r.data.filter(l => l.homework && l.homework.trim());
      setLogs(withHw);
    }).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  const getStatus = (l) => {
    if (l.homework_due_date && String(l.homework_due_date) < today) return 'overdue';
    return 'pending';
  };

  const filtered = logs.filter(l => {
    const matchName = !search.trim() || (l.student_name || '').toLowerCase().includes(search.trim().toLowerCase());
    const s = getStatus(l);
    const matchStatus = statusFilter === 'all' || s === statusFilter;
    return matchName && matchStatus;
  });

  const counts = { all: logs.length, pending: logs.filter(l => getStatus(l) === 'pending').length, overdue: logs.filter(l => getStatus(l) === 'overdue').length };

  const statusStyle = { pending: { bg:'rgba(251,191,36,0.15)', color:'#d97706', border:'rgba(251,191,36,0.3)', label:'Pending' }, overdue: { bg:'rgba(220,38,38,0.1)', color:'#dc2626', border:'rgba(220,38,38,0.2)', label:'Overdue' } };

  return (
    <div style={{ padding:'28px 24px', maxWidth:900, margin:'0 auto' }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'20px 24px', border:'1px solid #e8edf8', boxShadow:'0 4px 16px rgba(37,84,184,0.06)', marginBottom:22 }}>
        <h3 style={{ fontWeight:800, fontSize:18, color:'#0f1f45', marginBottom:16 }}>📋 Assigned Homework</h3>
        <p style={{ fontSize:13, color:'#64748b', marginTop:-8, marginBottom:16 }}>Students share completed homework with you in chat.</p>

        {/* Summary pills */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:18 }}>
          {[['all','All','#2554b8','#eef3ff'],['pending','Pending','#d97706','#fffbeb'],['overdue','Overdue','#dc2626','#fef2f2']].map(([k,label,color,bg]) => (
            <button key={k} onClick={() => setStatusFilter(k)}
              style={{ padding:'7px 16px', borderRadius:20, border:`2px solid ${statusFilter===k ? color : '#e2e8f0'}`, background: statusFilter===k ? bg : '#fff', color: statusFilter===k ? color : '#64748b', fontWeight:700, fontSize:12, cursor:'pointer' }}>
              {label} <span style={{ background: statusFilter===k ? color : '#e2e8f0', color: statusFilter===k ? '#fff' : '#64748b', borderRadius:10, padding:'1px 7px', marginLeft:4, fontSize:11 }}>{counts[k]}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ position:'relative', maxWidth:360 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:15, color:'#94a3b8' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by student name…"
            style={{ width:'100%', padding:'10px 12px 10px 36px', borderRadius:10, border:'1.5px solid #e2e8f0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ background:'#fff', borderRadius:16, padding:48, textAlign:'center', border:'1px solid #e8edf8' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
          <p style={{ color:'#64748b', fontSize:15 }}>No homework records found</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {filtered.map(l => {
            const s = getStatus(l);
            const st = statusStyle[s];
            return (
              <div key={l.id} style={{ background:'#fff', borderRadius:16, padding:'16px 20px', border:'1px solid #e8edf8', boxShadow:'0 2px 8px rgba(37,84,184,0.05)', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>{l.student_name || '—'}</span>
                    <span style={{ fontSize:11, color:'#2554b8', fontWeight:700, background:'#eef3ff', padding:'2px 10px', borderRadius:10 }}>{l.subject}</span>
                  </div>
                  <div style={{ fontSize:13, color:'#334155', marginBottom:8, lineHeight:1.5, wordBreak:'break-word' }}>{l.homework}</div>
                  <div style={{ display:'flex', gap:14, fontSize:11.5, color:'#94a3b8', flexWrap:'wrap' }}>
                    {l.schedule_date && <span>📅 Session: {String(l.schedule_date)}</span>}
                    {l.homework_due_date && <span>⏰ Due: <strong style={{ color: s==='overdue' ? '#dc2626' : '#475569' }}>{String(l.homework_due_date)}</strong></span>}
                  </div>
                </div>
                <span style={{ padding:'5px 14px', borderRadius:20, fontSize:12, fontWeight:700, background:st.bg, color:st.color, border:`1px solid ${st.border}`, flexShrink:0 }}>{st.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SETTINGS
═══════════════════════════════════════════════════ */
function Settings() {
  const [profile, setProfile] = useState(null);
  useEffect(() => { API.get('/auth/me').then(r=>setProfile(r.data)).catch(()=>{}); }, []);
  if (!profile) return <LoadingState />;
  const subjects = profile.selected_subjects ? profile.selected_subjects.split(',').map(s=>s.trim()).filter(Boolean) : [];
  const colors = ['#2554b8','#7c3aed','#16a34a','#d97706','#dc2626','#0891b2'];

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      <div style={{ maxWidth:680 }}>
        <div style={{ ...card({ padding:0, overflow:'hidden', marginBottom:20 }) }}>
          <div style={{ background:'linear-gradient(135deg,#1a3c8f,#2554b8)', padding:'28px 28px 0', position:'relative' }}>
            <div style={{ display:'flex', alignItems:'flex-end', gap:18, paddingBottom:24 }}>
              <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(255,255,255,0.2)', border:'4px solid rgba(255,255,255,0.35)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, fontWeight:800, color:'#fff', flexShrink:0 }}>{profile.name?.[0]?.toUpperCase()}</div>
              <div>
                <h3 style={{ color:'#fff', fontWeight:800, fontSize:20 }}>{profile.name}</h3>
                <p style={{ color:'rgba(255,255,255,0.65)', fontSize:13, marginTop:3 }}>{profile.email} · {profile.uid||''}</p>
                <span style={{ display:'inline-block', background:'rgba(255,255,255,0.2)', color:'#fff', fontSize:11, fontWeight:700, padding:'3px 12px', borderRadius:20, marginTop:6, border:'1px solid rgba(255,255,255,0.3)' }}>🎓 Companion ·{profile.gender||'N/A'}</span>
              </div>
            </div>
          </div>
          <div style={{ padding:'22px 28px' }}>
            <div style={{ marginBottom:20 }}>
              <h4 style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:10 }}>Bio</h4>
              <p style={{ fontSize:14, color: profile.bio ? '#1e293b':'#94a3b8', background:'#f8faff', padding:'13px 16px', borderRadius:10, border:'1px solid #eef2fa', lineHeight:1.7 }}>{profile.bio||'No Bio Available'}</p>
            </div>
            <div style={{ marginBottom:20 }}>
              <h4 style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:10 }}>Contact Details</h4>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                {[['Address', profile.address||'Not provided'],['Alternate Email', profile.alt_email||'Not provided'],['Phone', profile.phone||'Not provided']].map(([k,v])=>(
                  <div key={k} style={{ background:'#f8faff', padding:'13px 16px', borderRadius:10, border:'1px solid #eef2fa' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1e293b' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            {subjects.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <h4 style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:10 }}>Subjects</h4>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {subjects.map((s,i)=><span key={s} style={{ padding:'6px 16px', borderRadius:50, fontWeight:700, fontSize:13, color:'#fff', background:colors[i%colors.length], boxShadow:`0 3px 10px ${colors[i%colors.length]}44` }}>{s}</span>)}
                </div>
              </div>
            )}
            <div style={{ padding:'11px 16px', background:'linear-gradient(135deg,#fffbeb,#fef3c7)', borderRadius:10, border:'1px solid #fde68a', fontSize:13, color:'#92400e', fontWeight:600 }}>
              ⚠️ Please contact the admin for any changes.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   HELP & SUPPORT
═══════════════════════════════════════════════════ */
function HelpSupport() {
  return (
    <div style={{ padding:'28px 32px', display:'flex', alignItems:'center', justifyContent:'center', minHeight:400 }} className="page-enter">
      <div style={{ ...card({ textAlign:'center', padding:48, maxWidth:460 }) }}>
        <div style={{ fontSize:60, marginBottom:16 }}>🤝</div>
        <h2 style={{ fontWeight:800, fontSize:22, color:'#0f1f45', marginBottom:8 }}>Help and Support</h2>
        <p style={{ color:'#64748b', marginBottom:28, lineHeight:1.7 }}>If you need assistance, feel free to reach out to us through the following channels:</p>
        <div style={{ background:'linear-gradient(135deg,#f0f5ff,#e8eeff)', borderRadius:16, padding:'22px 28px', textAlign:'left', border:'1px solid #dbeafe' }}>
          <h4 style={{ fontWeight:800, fontSize:14, color:'#0f1f45', marginBottom:14 }}>Contact Information</h4>
          {[['📧','Email','info@sapienceconnect.com'],['📞','Phone','+918078406608']].map(([icon,label,val])=>(
            <div key={label} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, fontSize:14 }}>
              <span style={{ fontSize:18 }}>{icon}</span>
              <span style={{ color:'#64748b', fontWeight:600 }}>{label}:</span>
              <span style={{ color:'#2554b8', fontWeight:700 }}>{val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   TUTOR PAYMENT DETAILS
═══════════════════════════════════════════════════ */
function TutorPaymentDetails() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [breakdown, setBreakdown] = useState(null);
  const [payHistory, setPayHistory] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [tab, setTab] = useState('summary');
  const [concern, setConcern] = useState({ subject: '', message: '' });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [b, h, c, a] = await Promise.all([
        API.get(`/payments/my-student-breakdown?start_date=${startDate}&end_date=${endDate}`),
        API.get('/payments/my-payment-history'),
        API.get('/payments/concerns/my'),
        API.get('/tutor-adjustments/mine').catch(() => ({ data: [] })),
      ]);
      setBreakdown(b.data);
      setPayHistory(h.data);
      setConcerns(c.data);
      setAdjustments(a.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const submitConcern = async () => {
    if (!concern.subject.trim() || !concern.message.trim()) { setMsg('Please fill subject and message'); return; }
    try {
      await API.post('/payments/concerns', concern);
      setMsg('✓ Concern submitted');
      setConcern({ subject: '', message: '' });
      const c = await API.get('/payments/concerns/my');
      setConcerns(c.data);
    } catch { setMsg('Error submitting concern'); }
  };

  const tabStyle = t => ({
    padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13,
    cursor: 'pointer', transition: 'all 0.18s',
    background: tab === t ? '#2554b8' : 'transparent',
    color: tab === t ? '#fff' : '#64748b',
    boxShadow: tab === t ? '0 3px 10px rgba(37,84,184,0.3)' : 'none',
  });

  return (
    <div style={{ padding: '28px 32px' }} className="page-enter">
      {/* Date filter */}
      <div style={{ ...card(), padding: '18px 24px', marginBottom: 22, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 5 }}>From</label>
          <input type="date" className="t-input" value={startDate} onChange={e => setStartDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 600 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 5 }}>To</label>
          <input type="date" className="t-input" value={endDate} onChange={e => setEndDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 600 }} />
        </div>
        <button onClick={loadData} disabled={loading}
          style={{ padding: '9px 22px', background: 'linear-gradient(135deg,#2554b8,#3b82f6)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          {loading ? 'Loading…' : '🔍 Filter'}
        </button>
        {breakdown && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>HOURS (PERIOD)</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#2554b8' }}>{Number(breakdown.total_hours || 0).toFixed(2)}h</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>AMOUNT (PERIOD)</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f1f45' }}>₹{Number(breakdown.total_amount || 0).toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>TOTAL CREDITED</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#16a34a' }}>₹{Number(breakdown.total_credited).toFixed(2)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: '#f1f5f9', padding: 5, borderRadius: 10, flexWrap: 'wrap' }}>
        <button style={tabStyle('summary')} onClick={() => setTab('summary')}>📊 Student Summary</button>
        <button style={tabStyle('history')} onClick={() => setTab('history')}>💳 Payment History</button>
        <button style={tabStyle('adjustments')} onClick={() => setTab('adjustments')}>
          🔧 Adjustments
          {adjustments.length > 0 && (
            <span style={{ background: '#2554b8', color: '#fff', fontSize: 10, borderRadius: 10, padding: '1px 6px', marginLeft: 6 }}>
              {adjustments.length}
            </span>
          )}
        </button>
        <button style={tabStyle('concerns')} onClick={() => setTab('concerns')}>
          🚨 Concerns
          {concerns.filter(c => c.status === 'pending').length > 0 && (
            <span style={{ background: '#dc2626', color: '#fff', fontSize: 10, borderRadius: 10, padding: '1px 6px', marginLeft: 6 }}>
              {concerns.filter(c => c.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {/* Tab: Student Summary */}
      {tab === 'summary' && (
        <div style={card({ padding: 0, overflow: 'hidden' })}>
          {!breakdown || breakdown.students.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 52, color: '#94a3b8' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
              <p style={{ fontWeight: 600 }}>No completed classes in this period</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ background: '#f8faff' }}>
                  {['Student', 'Class', 'Subject', 'Mode', 'Rate/hr', 'Hours', 'Amount'].map(h => (
                    <th key={h} style={{ padding: '12px 18px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {breakdown.students.map(s => {
                  // Backwards-compatibility: if backend hasn't been redeployed yet
                  // and subject_breakdown is missing, fall back to one summary row.
                  const rows = s.subject_breakdown && s.subject_breakdown.length > 0
                    ? s.subject_breakdown
                    : [{ subject: (s.subjects || []).join(', '), mode: '—', rate_per_hour: 0, hours: s.total_hours, amount: s.total_amount || 0 }];
                  return rows.map((row, idx) => (
                    <tr key={`${s.student_id}-${idx}`} style={{ borderTop: '1px solid #eef2fa', background: idx === 0 ? '#fff' : '#fbfcff' }}>
                      <td style={{ padding: '12px 18px', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>{idx === 0 ? s.student_name : ''}</td>
                      <td style={{ padding: '12px 18px', color: '#475569', fontSize: 13, whiteSpace: 'nowrap' }}>{idx === 0 ? s.student_class : ''}</td>
                      <td style={{ padding: '12px 18px', color: '#475569', fontSize: 13 }}>{row.subject}</td>
                      <td style={{ padding: '12px 18px', color: '#475569', fontSize: 12, textTransform: 'capitalize' }}>{row.mode}</td>
                      <td style={{ padding: '12px 18px', color: '#475569', fontSize: 13, whiteSpace: 'nowrap' }}>₹{Number(row.rate_per_hour || 0).toFixed(2)}</td>
                      <td style={{ padding: '12px 18px', fontWeight: 700, color: '#2554b8', fontSize: 14, whiteSpace: 'nowrap' }}>{Number(row.hours || 0).toFixed(2)}h</td>
                      <td style={{ padding: '12px 18px', fontWeight: 800, color: '#16a34a', fontSize: 14, whiteSpace: 'nowrap' }}>₹{Number(row.amount || 0).toFixed(2)}</td>
                    </tr>
                  ));
                })}
                <tr style={{ borderTop: '2px solid #c7d4f5', background: '#f0f5ff' }}>
                  <td colSpan={5} style={{ padding: '13px 18px', fontWeight: 800, color: '#0f1f45', fontSize: 13, textAlign: 'right' }}>Period Total</td>
                  <td style={{ padding: '13px 18px', fontWeight: 900, color: '#2554b8', fontSize: 15, whiteSpace: 'nowrap' }}>{Number(breakdown.total_hours || 0).toFixed(2)}h</td>
                  <td style={{ padding: '13px 18px', fontWeight: 900, color: '#0f1f45', fontSize: 15, whiteSpace: 'nowrap' }}>₹{Number(breakdown.total_amount || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Payment History */}
      {tab === 'history' && (
        <div style={card({ padding: 0, overflow: 'hidden' })}>
          {(() => {
            const filteredHistory = payHistory.filter(r =>
              r.start_date <= endDate && r.end_date >= startDate
            );
            return filteredHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 52, color: '#94a3b8' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💳</div>
                <p style={{ fontWeight: 600 }}>No payment records for this period</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
                <thead>
                  <tr style={{ background: '#f8faff' }}>
                    {['Period', 'Hours', 'Amount', 'Paid On'].map(h => (
                      <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid #eef2fa' }}>
                      <td style={{ padding: '13px 20px', fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>{r.start_date} → {r.end_date}</td>
                      <td style={{ padding: '13px 20px', fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>{r.total_hours}h</td>
                      <td style={{ padding: '13px 20px', fontWeight: 800, fontSize: 16, color: '#16a34a', whiteSpace: 'nowrap' }}>₹{r.total_amount.toFixed(2)}</td>
                      <td style={{ padding: '13px 20px', fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.paid_on || r.paid_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Tab: Adjustments */}
      {tab === 'adjustments' && (
        <div style={card({ padding: 0, overflow: 'hidden' })}>
          {adjustments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 52, color: '#94a3b8' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔧</div>
              <p style={{ fontWeight: 600 }}>No payment adjustments recorded</p>
              <p style={{ fontSize: 12, marginTop: 6 }}>Bonuses, deposits and corrections set by admin will appear here.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                <thead>
                  <tr style={{ background: '#f8faff' }}>
                    {['Month', 'Category', 'Reason', 'Amount', 'Added On'].map(h => (
                      <th key={h} style={{ padding: '12px 18px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map(a => {
                    const label = ({
                      deposit_hold: 'Deposit Hold', deposit_release: 'Deposit Release',
                      bonus: 'Bonus', deduction: 'Deduction', correction: 'Correction', other: 'Other',
                    })[a.category] || a.category;
                    return (
                      <tr key={a.id} style={{ borderTop: '1px solid #eef2fa' }}>
                        <td style={{ padding: '12px 18px', fontSize: 13, color: '#475569', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{a.applied_month}</td>
                        <td style={{ padding: '12px 18px' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#eef2ff', color: '#4338ca', whiteSpace: 'nowrap' }}>{label}</span>
                        </td>
                        <td style={{ padding: '12px 18px', fontSize: 13, color: '#1e293b' }}>{a.reason}</td>
                        <td style={{ padding: '12px 18px', fontWeight: 800, fontSize: 15, color: a.amount >= 0 ? '#16a34a' : '#dc2626', whiteSpace: 'nowrap' }}>
                          {a.amount >= 0 ? '+' : '−'}₹{Math.abs(a.amount).toFixed(2)}
                        </td>
                        <td style={{ padding: '12px 18px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          {a.created_at ? new Date(a.created_at + 'Z').toLocaleDateString() : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Concerns */}
      {tab === 'concerns' && (
        <div>
          {/* Raise new concern */}
          <div style={card({ marginBottom: 20 })}>
            <h4 style={{ fontWeight: 800, fontSize: 15, color: '#0f1f45', marginBottom: 14 }}>🚨 Raise a Payment Concern</h4>
            {msg && (
              <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: msg.startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', color: msg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight: 700, fontSize: 13 }}>
                {msg}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 5 }}>Subject</label>
              <input className="t-input" placeholder="e.g. Payment not received for March" value={concern.subject}
                onChange={e => setConcern(p => ({ ...p, subject: e.target.value }))}
                style={{ width: '100%', padding: '9px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 5 }}>Details</label>
              <textarea placeholder="Describe your concern in detail…" value={concern.message}
                onChange={e => setConcern(p => ({ ...p, message: e.target.value }))}
                rows={4} style={{ width: '100%', padding: '9px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
            <button onClick={submitConcern}
              style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#2554b8,#3b82f6)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Submit Concern
            </button>
          </div>

          {/* Previous concerns */}
          {concerns.length > 0 && (
            <div style={card({ padding: 0, overflow: 'hidden' })}>
              <div style={{ padding: '16px 22px', borderBottom: '1px solid #eef2fa', fontWeight: 800, fontSize: 14, color: '#0f1f45' }}>My Previous Concerns</div>
              {concerns.map(c => (
                <div key={c.id} style={{ padding: '16px 22px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f1f45' }}>{c.subject}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{c.created_at}</span>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.status === 'resolved' ? 'rgba(22,163,74,0.1)' : 'rgba(245,158,11,0.1)', color: c.status === 'resolved' ? '#16a34a' : '#d97706', border: `1px solid ${c.status === 'resolved' ? 'rgba(22,163,74,0.2)' : 'rgba(245,158,11,0.2)'}` }}>
                        {c.status === 'resolved' ? '✓ Resolved' : '⏳ Pending'}
                      </span>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: '#475569', marginBottom: c.admin_response ? 10 : 0 }}>{c.message}</p>
                  {c.admin_response && (
                    <div style={{ background: 'rgba(37,84,184,0.06)', borderLeft: '3px solid #2554b8', padding: '10px 14px', borderRadius: '0 8px 8px 0', marginTop: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#2554b8', marginBottom: 4 }}>ADMIN RESPONSE</div>
                      <p style={{ fontSize: 13, color: '#1e293b' }}>{c.admin_response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════
   TUTOR CHAT
═══════════════════════════════════════════════════ */
function TutorChat({ unreadRooms = {}, onRoomRead }) {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isMobile] = useState(() => window.innerWidth <= 768);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    API.get('/chat/rooms').then(r => setRooms(r.data || [])).catch(() => {});
  }, []);

  const selectRoom = (room) => {
    setSelectedRoom(room);
    if (room.last_message_id) {
      localStorage.setItem(`chat_seen_${room.room_id}`, String(room.last_message_id));
    }
    onRoomRead && onRoomRead();
    if (isMobile) setShowChat(true);
  };

  const RoomList = () => (
    <div style={{ background:'#fff', borderRadius:20, border:'1px solid #e8edf8', boxShadow:'0 4px 24px rgba(37,84,184,0.07)', overflowY:'auto', display:'flex', flexDirection:'column', height: isMobile ? 'calc(100vh - 120px)' : '100%' }}>
      <div style={{ padding:'16px 18px', borderBottom:'1px solid #f1f5f9', fontWeight:800, fontSize:13, color:'#0f1f45' }}>
        Conversations ({rooms.length})
      </div>
      {rooms.length === 0 ? (
        <div style={{ padding:24, textAlign:'center', color:'#94a3b8', fontSize:13 }}>No chat rooms yet</div>
      ) : rooms.map(room => {
        const hasUnread = unreadRooms[room.room_id];
        return (
          <div key={room.room_id} onClick={() => selectRoom(room)}
            style={{ padding:'13px 18px', cursor:'pointer', borderBottom:'1px solid #f8faff',
              background: selectedRoom?.room_id === room.room_id ? 'linear-gradient(135deg,#eef3ff,#e8eeff)' : hasUnread ? '#fffbf0' : '#fff',
              borderLeft: selectedRoom?.room_id === room.room_id ? '3px solid #2554b8' : hasUnread ? '3px solid #f59e0b' : '3px solid transparent',
              transition:'all 0.18s' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:3 }}>
              <div style={{ fontWeight: hasUnread ? 800 : 700, fontSize:13, color:'#0f1f45' }}>{room.student_name}</div>
              {hasUnread && <span style={{ width:8, height:8, borderRadius:'50%', background:'#f59e0b', flexShrink:0 }} />}
            </div>
            {room.last_message && <div style={{ fontSize:11, color: hasUnread ? '#92400e' : '#94a3b8', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontWeight: hasUnread ? 600 : 400 }}>{room.last_message}</div>}
            {room.last_message_at && <div style={{ fontSize:10, color:'#cbd5e1', marginTop:2 }}>{room.last_message_at}</div>}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '12px' : '28px 32px' }} className="page-enter">
      {isMobile ? (
        showChat && selectedRoom ? (
          <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 120px)' }}>
            <button onClick={() => setShowChat(false)}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0', background:'none', border:'none', cursor:'pointer', color:'#2554b8', fontWeight:700, fontSize:14, marginBottom:10 }}>
              ← Back to Conversations
            </button>
            <div style={{ flex:1, minHeight:0 }}>
              <ChatWindow roomId={selectedRoom.room_id} studentName={selectedRoom.student_name} />
            </div>
          </div>
        ) : <RoomList />
      ) : (
        <div style={{ display:'flex', gap:18, height:600 }}>
          <div style={{ width:260, flexShrink:0 }}><RoomList /></div>
          <div style={{ flex:1, minWidth:0 }}>
            <ChatWindow roomId={selectedRoom?.room_id || null} studentName={selectedRoom?.student_name} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   TUTOR CLASS REQUESTS
═══════════════════════════════════════════════════ */
function TutorClassRequests() {
  const [requests, setRequests] = useState([]);
  const [msg, setMsg] = useState('');
  const [actionModal, setActionModal] = useState(null); // { id, action }
  const [actionForm, setActionForm] = useState({ remarks:'', reassigned_date:'', reassigned_time:'' });
  const [chatWarning, setChatWarning] = useState(null); // { id } — shown before reassign modal

  const load = useCallback(() => {
    API.get('/class-request/for-tutor').then(r => setRequests(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusBadge = (s) => {
    const map = {
      pending:    { background:'rgba(217,119,6,0.1)',  color:'#d97706', border:'1px solid rgba(217,119,6,0.25)' },
      approved:   { background:'rgba(22,163,74,0.1)',  color:'#16a34a', border:'1px solid rgba(22,163,74,0.25)' },
      rejected:   { background:'rgba(220,38,38,0.1)',  color:'#dc2626', border:'1px solid rgba(220,38,38,0.25)' },
      reassigned: { background:'rgba(37,84,184,0.1)',  color:'#2554b8', border:'1px solid rgba(37,84,184,0.25)' },
    };
    return map[s] || { background:'#f1f5f9', color:'#64748b', border:'1px solid #e2e8f0' };
  };

  const pending = requests.filter(r => r.status === 'pending').length;

  return (
    <div style={{ padding:'28px 32px' }} className="page-enter">
      {pending > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 18px', borderRadius:14, background:'rgba(217,119,6,0.08)', border:'1.5px solid rgba(217,119,6,0.2)', marginBottom:20 }}>
          <span style={{ fontSize:18 }}>📋</span>
          <span style={{ fontWeight:700, fontSize:13, color:'#d97706' }}>{pending} pending class request{pending > 1 ? 's' : ''} awaiting your action</span>
        </div>
      )}
      {msg && (
        <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8, background: msg.startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', color: msg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight:700, fontSize:13, cursor:'pointer' }} onClick={() => setMsg('')}>
          {msg}
        </div>
      )}
      <div style={card({ padding:0, overflow:'hidden' })}>
        <div style={{ padding:'16px 22px', borderBottom:'1px solid #eef2fa', fontWeight:800, fontSize:14, color:'#0f1f45' }}>
          Class Requests ({requests.length})
        </div>
        <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:560 }}>
          <thead>
            <tr style={{ background:'#f8faff' }}>
              {['Student','Subject','Date','Time','Reason','Status','Action'].map(h => (
                <th key={h} style={{ padding:'12px 18px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign:'center', padding:32, color:'#94a3b8', fontSize:13 }}>No class requests yet</td></tr>
            ) : requests.map(r => (
              <tr key={r.id} style={{ borderTop:'1px solid #f1f5f9' }}>
                <td style={{ padding:'12px 18px', fontSize:13, color:'#1e293b', whiteSpace:'nowrap' }}>{r.student_name || r.student_id}</td>
                <td style={{ padding:'12px 18px', fontSize:13, color:'#1e293b', whiteSpace:'nowrap' }}>{r.subject}</td>
                <td style={{ padding:'12px 18px', fontSize:13, color:'#475569', whiteSpace:'nowrap' }}>{r.preferred_date}</td>
                <td style={{ padding:'12px 18px', fontSize:13, color:'#475569', whiteSpace:'nowrap' }}>{r.preferred_time ? fmtTime(r.preferred_time) : '—'}</td>
                <td style={{ padding:'12px 18px', fontSize:12, color:'#64748b', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.reason}</td>
                <td style={{ padding:'12px 18px' }}>
                  <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap', ...statusBadge(r.status) }}>{r.status}</span>
                </td>
                <td style={{ padding:'12px 18px' }}>
                  {r.status === 'pending' && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <button className="t-btn" style={{ background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', border:'none', borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                        onClick={() => { setActionModal({ id:r.id, action:'approved' }); setActionForm({ remarks:'', reassigned_date:'', reassigned_time:'' }); }}>Approve</button>
                      <button className="t-btn" style={{ background:'none', border:'1px solid #fca5a5', color:'#dc2626', borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                        onClick={() => { setActionModal({ id:r.id, action:'rejected' }); setActionForm({ remarks:'', reassigned_date:'', reassigned_time:'' }); }}>Reject</button>
                      <button className="t-btn" style={{ background:'none', border:'1px solid #93c5fd', color:'#2554b8', borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                        onClick={() => setChatWarning({ id:r.id })}>Reassign</button>
                    </div>
                  )}
                  {r.tutor_remarks && (
                    <div style={{ fontSize:11, color:'#64748b', marginTop:4, fontStyle:'italic', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>"{r.tutor_remarks}"</div>
                  )}
                  {r.status === 'reassigned' && r.reassigned_date && (
                    <div style={{ fontSize:11, color:'#2554b8', marginTop:3 }}>→ {r.reassigned_date}{r.reassigned_time ? ` ${fmtTime(r.reassigned_time)}` : ''}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {/* Chat-first warning before Reassign */}
      {chatWarning && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={() => setChatWarning(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:20, padding:32, width:420, boxShadow:'0 20px 60px rgba(0,0,0,0.18)', textAlign:'center' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>💬</div>
            <h3 style={{ fontWeight:800, fontSize:18, color:'#0f1f45', marginBottom:10 }}>Discuss First in Chat</h3>
            <p style={{ fontSize:13, color:'#64748b', marginBottom:24, lineHeight:1.6 }}>
              Before reassigning the time, please <strong>discuss the new schedule with the student via Chat</strong> to confirm they are available. Once agreed, proceed to reassign.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button onClick={() => setChatWarning(null)}
                style={{ padding:'9px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                Cancel
              </button>
              <button onClick={() => {
                setChatWarning(null);
                setActionModal({ id:chatWarning.id, action:'reassigned' });
                setActionForm({ remarks:'', reassigned_date:'', reassigned_time:'' });
              }} style={{ padding:'9px 20px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                Continue to Reassign →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {actionModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={() => setActionModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:20, padding:28, width:440, boxShadow:'0 20px 60px rgba(0,0,0,0.18)' }}>
            <h3 style={{ marginBottom:16, fontWeight:800 }}>
              {actionModal.action === 'approved' ? '✅ Approve Request' : actionModal.action === 'rejected' ? '❌ Reject Request' : '🔄 Reassign Time'}
            </h3>
            {actionModal.action === 'reassigned' && (
              <>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>New Date</label>
                  <input type="date" className="form-control" value={actionForm.reassigned_date} onChange={e => setActionForm(f => ({...f, reassigned_date:e.target.value}))} />
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>New Time</label>
                  <TimePicker12h className="form-control" value={actionForm.reassigned_time} onChange={v => setActionForm(f => ({...f, reassigned_time:v}))} />
                </div>
              </>
            )}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Remarks (optional)</label>
              <textarea className="form-control" rows={3} placeholder="Add a note for the student…" value={actionForm.remarks} onChange={e => setActionForm(f => ({...f, remarks:e.target.value}))} style={{ resize:'vertical' }} />
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setActionModal(null)} style={{ padding:'8px 18px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontWeight:700 }}>Cancel</button>
              <button onClick={async () => {
                try {
                  await API.patch(`/class-request/${actionModal.id}`, {
                    status: actionModal.action,
                    tutor_remarks: actionForm.remarks || null,
                    reassigned_date: actionForm.reassigned_date || null,
                    reassigned_time: actionForm.reassigned_time || null,
                  });
                  setMsg(`✓ Request ${actionModal.action}`);
                  setActionModal(null);
                  load();
                } catch(e) { setMsg(e.response?.data?.detail || 'Error'); }
              }} style={{ padding:'8px 18px', borderRadius:8, border:'none', background: actionModal.action === 'rejected' ? '#dc2626' : '#16a34a', color:'#fff', cursor:'pointer', fontWeight:700 }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Shared components ── */
function LoadingState() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300 }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:44, height:44, border:'4px solid #dbeafe', borderTopColor:'#2554b8', borderRadius:'50%', margin:'0 auto 14px', animation:'spin 0.9s linear infinite' }} />
        <p style={{ color:'#64748b', fontWeight:600, fontSize:14 }}>Loading…</p>
      </div>
    </div>
  );
}
function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign:'center', padding:'52px 20px', color:'#94a3b8' }}>
      <div style={{ fontSize:48, marginBottom:12, opacity:0.6 }}>{icon}</div>
      <p style={{ fontWeight:600, fontSize:14 }}>{text}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   TUTOR FEEDBACK VIEW
═══════════════════════════════════════════════════ */
function TutorFeedbackView() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    API.get('/feedback/all').then(r => setRows(r.data || [])).catch(() => {});
  }, []);

  const stars = n => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : '—';

  return (
    <>
      <div className="page-header" style={{ background:'#fff', borderBottom:'1px solid #e8edf8', padding:'16px 24px' }}>
        <div><h2 style={{ fontWeight:800, fontSize:20, color:'#0f1f45' }}>⭐ Student Feedback</h2>
        <p style={{ color:'#64748b', fontSize:13 }}>Feedback submitted by your students</p></div>
      </div>
      <div style={{ padding:'20px 24px' }}>
        {rows.length === 0 ? (
          <div style={{ background:'#fff', borderRadius:16, padding:40, textAlign:'center', boxShadow:'0 4px 16px rgba(37,84,184,0.08)' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>⭐</div>
            <p style={{ color:'#64748b', fontSize:15 }}>No feedback from students yet</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {rows.map(r => (
              <div key={r.id} style={{ background:'#fff', borderRadius:16, padding:20, boxShadow:'0 4px 16px rgba(37,84,184,0.08)', border:'1px solid #e8edf8' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8, marginBottom:10 }}>
                  <div>
                    <span style={{ fontWeight:700, fontSize:14, color:'#0f1f45' }}>{r.student_name}</span>
                    {r.subject && <span style={{ color:'#64748b', fontSize:12, marginLeft:10 }}>• {r.subject}</span>}
                  </div>
                  <span style={{ fontSize:12, color:'#94a3b8' }}>{r.submitted_at?.slice(0,10)} • {r.feedback_week}</span>
                </div>
                <div style={{ display:'flex', gap:16, fontSize:13, flexWrap:'wrap', marginBottom: (r.parent_feedback || r.additional_notes) ? 10 : 0 }}>
                  {r.topic_understanding && <span style={{ color:'#f59e0b' }}>📖 {stars(r.topic_understanding)}</span>}
                  {r.difficulty_level && <span style={{ color:'#f59e0b' }}>🔥 {stars(r.difficulty_level)}</span>}
                  {r.teacher_support && <span style={{ color:'#f59e0b' }}>🤝 {stars(r.teacher_support)}</span>}
                  {r.overall_experience && <span style={{ color:'#f59e0b' }}>⭐ {stars(r.overall_experience)}</span>}
                </div>
                {r.parent_feedback && <p style={{ fontSize:12, color:'#64748b', fontStyle:'italic', marginTop:6 }}>👨‍👩‍👧 Parent: "{r.parent_feedback}"</p>}
                {r.additional_notes && <p style={{ fontSize:12, color:'#64748b', marginTop:4 }}>📝 {r.additional_notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════
   COMPANION QUERIES
═══════════════════════════════════════════════════ */
function VRLearning() {
  const [students, setStudents] = useState([]);
  const [contents, setContents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ student_id: '', class_name: '', request_date: '', vr_content: '', has_doubt: false, doubt_text: '' });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    API.get('/vr/my-students').then(r => setStudents(r.data)).catch(() => {});
    API.get('/vr/content').then(r => setContents(r.data)).catch(() => {});
    API.get('/vr/requests').then(r => setRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onStudent = (id) => {
    const s = students.find(x => String(x.student_id) === String(id));
    setForm(f => ({ ...f, student_id: id, class_name: s?.class_name || f.class_name }));
  };

  const viewPdf = async (c) => {
    try {
      const response = await API.get(`/vr/content/${c.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { setMsg('Could not open PDF. Please try again.'); }
  };

  const submit = async () => {
    if (!form.student_id) { setMsg('Please select a student'); return; }
    if (!form.vr_content.trim()) { setMsg('Please enter the VR content / topic'); return; }
    if (form.has_doubt && !form.doubt_text.trim()) { setMsg('Please describe your doubt'); return; }
    setLoading(true);
    try {
      await API.post('/vr/requests', {
        student_id: parseInt(form.student_id),
        class_name: form.class_name.trim() || null,
        request_date: form.request_date || null,
        vr_content: form.vr_content.trim(),
        has_doubt: form.has_doubt,
        doubt_text: form.has_doubt ? form.doubt_text.trim() : null,
      });
      setMsg('✓ VR set request submitted!');
      setForm({ student_id: '', class_name: '', request_date: '', vr_content: '', has_doubt: false, doubt_text: '' });
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error submitting request');
    } finally { setLoading(false); }
  };

  const badge = (st) => {
    const m = { pending:{bg:'#fffbeb',c:'#d97706',l:'Pending'}, fulfilled:{bg:'#f0fdf4',c:'#16a34a',l:'Fulfilled'}, rejected:{bg:'#fef2f2',c:'#dc2626',l:'Rejected'} }[st] || {bg:'#f1f5f9',c:'#64748b',l:st};
    return <span style={{ fontSize:11, fontWeight:800, padding:'3px 10px', borderRadius:20, background:m.bg, color:m.c }}>{m.l}</span>;
  };
  const inp = { width:'100%', padding:'10px 12px', border:'1.5px solid #e2e8f6', borderRadius:10, fontSize:14, fontFamily:'inherit', boxSizing:'border-box' };
  const lbl = { fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, display:'block' };

  return (
    <>
      <div className="page-header"><div><h2>VR Learning</h2><p>Review VR content and request a VR set for your students</p></div></div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}

        {/* Available VR Content */}
        <div style={{ background:'#fff', border:'1px solid #e8edf8', borderRadius:16, padding:20, marginBottom:20 }}>
          <h4 style={{ fontWeight:800, color:'#0f1f45', fontSize:15, margin:'0 0 4px' }}>🎬 Available VR Content</h4>
          <p style={{ fontSize:13, color:'#64748b', margin:'0 0 14px' }}>Review the available VR contents before requesting a set.</p>
          {contents.length === 0 ? (
            <div style={{ fontSize:13, color:'#94a3b8', padding:'12px 0' }}>No VR content uploaded yet.</div>
          ) : (
            <div style={{ display:'grid', gap:10 }}>
              {contents.map(c => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'12px 14px', border:'1px solid #eef2fb', borderRadius:12, background:'#fafbff' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                    <span style={{ fontSize:20 }}>📄</span>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontWeight:700, color:'#1e293b', fontSize:14, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.title}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{c.file_name}</div>
                    </div>
                  </div>
                  <button onClick={() => viewPdf(c)} style={{ padding:'8px 16px', background:'#2554b8', color:'#fff', border:'none', borderRadius:9, fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>View PDF</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* VR Set Request form */}
        <div style={{ background:'#fff', border:'1px solid #e8edf8', borderRadius:16, padding:20, marginBottom:20 }}>
          <h4 style={{ fontWeight:800, color:'#0f1f45', fontSize:15, margin:'0 0 14px' }}>🕶️ Request a VR Set</h4>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14 }}>
            <div>
              <label style={lbl}>Student *</label>
              <select style={inp} value={form.student_id} onChange={e => onStudent(e.target.value)}>
                <option value="">Select a student</option>
                {students.map(s => <option key={s.student_id} value={s.student_id}>{s.student_name}{s.subject ? ` · ${s.subject}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Class</label>
              <input style={inp} value={form.class_name} onChange={e => setForm(f => ({ ...f, class_name: e.target.value }))} placeholder="e.g. Class 8" />
            </div>
            <div>
              <label style={lbl}>Date</label>
              <input type="date" style={inp} value={form.request_date} onChange={e => setForm(f => ({ ...f, request_date: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginTop:14 }}>
            <label style={lbl}>VR Content / Topic *</label>
            <textarea style={{ ...inp, minHeight:70, resize:'vertical' }} value={form.vr_content} onChange={e => setForm(f => ({ ...f, vr_content: e.target.value }))} placeholder="Which VR content / topic do you need?" />
          </div>
          <div style={{ marginTop:14 }}>
            <label style={lbl}>Any doubt regarding the VR set?</label>
            <div style={{ display:'flex', gap:10 }}>
              {[['No', false], ['Yes', true]].map(([t, v]) => (
                <button key={t} onClick={() => setForm(f => ({ ...f, has_doubt: v }))} style={{ padding:'8px 20px', borderRadius:9, fontWeight:700, fontSize:13, cursor:'pointer', border:`1.5px solid ${form.has_doubt === v ? '#2554b8' : '#e2e8f6'}`, background: form.has_doubt === v ? '#2554b8' : '#fff', color: form.has_doubt === v ? '#fff' : '#64748b' }}>{t}</button>
              ))}
            </div>
          </div>
          {form.has_doubt && (
            <div style={{ marginTop:14 }}>
              <label style={lbl}>Describe your doubt *</label>
              <textarea style={{ ...inp, minHeight:60, resize:'vertical' }} value={form.doubt_text} onChange={e => setForm(f => ({ ...f, doubt_text: e.target.value }))} placeholder="What is your doubt / issue about the VR set?" />
            </div>
          )}
          <button onClick={submit} disabled={loading} style={{ marginTop:16, padding:'11px 28px', background: loading ? '#93a5cc' : '#2554b8', color:'#fff', border:'none', borderRadius:10, fontWeight:800, fontSize:14, cursor: loading ? 'default' : 'pointer' }}>{loading ? 'Submitting…' : 'Submit Request'}</button>
        </div>

        {/* My Requests */}
        <div style={{ background:'#fff', border:'1px solid #e8edf8', borderRadius:16, padding:20 }}>
          <h4 style={{ fontWeight:800, color:'#0f1f45', fontSize:15, margin:'0 0 14px' }}>📋 My VR Requests</h4>
          {requests.length === 0 ? (
            <div style={{ fontSize:13, color:'#94a3b8' }}>No requests yet.</div>
          ) : (
            <div style={{ display:'grid', gap:12 }}>
              {requests.map(r => (
                <div key={r.id} style={{ border:'1px solid #eef2fb', borderRadius:12, padding:'14px 16px' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:6 }}>
                    <span style={{ fontWeight:800, color:'#2554b8', fontSize:13 }}>{r.ticket_id}</span>
                    {badge(r.status)}
                  </div>
                  <div style={{ fontSize:13, color:'#475569', marginBottom:4 }}>👤 <strong>{r.student_name || '—'}</strong>{r.class_name ? ` · ${r.class_name}` : ''}{r.request_date ? ` · 📅 ${r.request_date}` : ''}</div>
                  <div style={{ fontSize:13, color:'#1e293b', marginBottom:4 }}>🎬 {r.vr_content}</div>
                  {r.has_doubt && <div style={{ fontSize:12, color:'#d97706', marginBottom:4 }}>❓ Doubt: {r.doubt_text}</div>}
                  {r.admin_reply && <div style={{ fontSize:12, color:'#16a34a', background:'#f0fdf4', borderRadius:8, padding:'8px 10px', marginTop:6 }}>💬 Admin: {r.admin_reply}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function CompanionQueries() {
  const [enrollments, setEnrollments] = useState([]);
  const [queries, setQueries] = useState([]);
  const [form, setForm] = useState({ student_id: '', message: '', image_data: null, image_filename: null });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState({});
  const [replyLoading, setReplyLoading] = useState({});

  const load = useCallback(() => {
    API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {});
    API.get('/companion-queries/').then(r => setQueries(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && !/\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name)) {
      setMsg('Only image files allowed'); return;
    }
    try {
      const dataUrl = await compressImage(file, 1920, 0.85);
      setForm(f => ({ ...f, image_data: dataUrl, image_filename: (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg') }));
    } catch (err) {
      setMsg('Failed to read image — please try a different photo');
    }
  };

  const submit = async () => {
    if (!form.student_id) { setMsg('Please select a student'); return; }
    if (!form.message.trim()) { setMsg('Please enter your query'); return; }
    setLoading(true);
    try {
      await API.post('/companion-queries/', {
        student_id: parseInt(form.student_id),
        message: form.message.trim(),
        image_data: form.image_data || null,
        image_filename: form.image_filename || null,
      });
      setMsg('✓ Query submitted successfully!');
      setForm({ student_id: '', message: '', image_data: null, image_filename: null });
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error submitting query');
    } finally {
      setLoading(false);
    }
  };

  const myStudents = [...new Map(enrollments.map(e => [e.student_id, { id: e.student_id, name: e.student_name }])).values()];
  const adminMessages = queries.filter(q => q.admin_initiated);
  const myQueries = queries.filter(q => !q.admin_initiated);

  const submitTutorReply = async (q) => {
    const text = (replyText[q.id] || '').trim();
    if (!text) { setMsg('Reply cannot be empty'); return; }
    setReplyLoading(r => ({ ...r, [q.id]: true }));
    try {
      await API.patch(`/companion-queries/${q.id}/tutor-reply`, { reply: text });
      setMsg('✓ Reply sent to admin');
      setReplyText(r => ({ ...r, [q.id]: '' }));
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed to send reply'); }
    finally { setReplyLoading(r => ({ ...r, [q.id]: false })); }
  };

  return (
    <>
      <div className="page-header">
        <div><h2>My Queries</h2><p>Raise and track queries, and view messages from admin</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}

        {/* Admin messages to tutor */}
        {adminMessages.length > 0 && (
          <div className="card" style={{ marginBottom: 20, border: '1.5px solid #bfdbfe' }}>
            <div className="card-title" style={{ color: '#1e40af' }}>📨 Messages from Admin ({adminMessages.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {adminMessages.map(q => (
                <div key={q.id} style={{ border: `1.5px solid ${q.status === 'resolved' ? '#86efac' : '#bfdbfe'}`, borderRadius: 12, padding: '16px 18px', background: q.status === 'resolved' ? '#f0fdf4' : '#eff6ff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#1e40af' }}>{q.ticket_id}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: q.status === 'resolved' ? '#dcfce7' : '#dbeafe', color: q.status === 'resolved' ? '#15803d' : '#1e40af' }}>{q.status}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(q.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div style={{ padding: '8px 12px', borderRadius: 8, background: '#dbeafe', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', marginBottom: 3 }}>Admin</div>
                    <div style={{ fontSize: 13, color: '#1e3a8a' }}>{q.message}</div>
                  </div>
                  {q.admin_reply ? (
                    <div style={{ padding: '8px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #86efac' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 3 }}>Your Reply</div>
                      <div style={{ fontSize: 13, color: '#166534' }}>{q.admin_reply}</div>
                    </div>
                  ) : (
                    <div>
                      <textarea className="form-control" rows={3} maxLength={2000} placeholder="Type your reply to admin…"
                        value={replyText[q.id] || ''} onChange={e => setReplyText(r => ({ ...r, [q.id]: e.target.value }))} style={{ marginBottom: 8 }} />
                      <button className="btn btn-primary" style={{ fontSize: 13 }} disabled={replyLoading[q.id]} onClick={() => submitTutorReply(q)}>
                        {replyLoading[q.id] ? 'Sending…' : 'Send Reply'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title">Raise a New Query</div>
          <div className="form-group">
            <label>Student</label>
            <select className="form-control" value={form.student_id} onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}>
              <option value="">— Select Student —</option>
              {myStudents.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Query</label>
            <textarea className="form-control" rows={4} maxLength={2000} placeholder="Describe your query…" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
            <small style={{ color: 'var(--text-muted)' }}>{form.message.length}/2000</small>
          </div>
          <div className="form-group">
            <label>Attach Image <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional, max 5 MB)</span></label>
            <input type="file" accept="image/*" onChange={handleImage} className="form-control" />
            {form.image_data && (
              <div style={{ marginTop: 8 }}>
                <img src={form.image_data} alt="preview" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, border: '1px solid var(--border-color)' }} />
                <button onClick={() => setForm(f => ({ ...f, image_data: null, image_filename: null }))} style={{ marginLeft: 10, background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: 13 }}>Remove</button>
              </div>
            )}
          </div>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>{loading ? 'Submitting…' : 'Submit Query'}</button>
        </div>
        <div className="card">
          <div className="card-title">My Query History ({myQueries.length})</div>
          {myQueries.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">❓</div><p>No queries yet</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {myQueries.map(q => (
                <div key={q.id} style={{ border: `1.5px solid ${q.status === 'resolved' ? '#86efac' : '#e2e8f6'}`, borderRadius: 12, padding: '16px 18px', background: q.status === 'resolved' ? '#f0fdf4' : '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#2554b8' }}>{q.ticket_id}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: q.status === 'resolved' ? '#dcfce7' : '#fef9c3', color: q.status === 'resolved' ? '#15803d' : '#92400e' }}>{q.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Student: <strong>{q.student_name}</strong> · {new Date(q.created_at).toLocaleDateString()}</div>
                  <div style={{ fontSize: 13, color: '#334155', marginBottom: 8 }}>{q.message}</div>
                  {q.image_data && <img src={q.image_data} alt="attachment" style={{ maxWidth: 220, maxHeight: 160, borderRadius: 8, border: '1px solid #e2e8f6', marginBottom: 8 }} />}
                  {q.admin_reply && (
                    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#2554b8', marginBottom: 4 }}>Admin Reply</div>
                      <div style={{ fontSize: 13, color: '#1e3a8a' }}>{q.admin_reply}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}


/* ═══════════════════════════════════════════════════
   ROOT
═══════════════════════════════════════════════════ */
function ForcePasswordChange() {
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!newPw || newPw.length < 8) { setMsg('Password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setMsg('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await API.post('/auth/tutor-set-password', { new_password: newPw });
      localStorage.setItem('force_password_change', '0');
      window.location.reload();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to set password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1a3c8f 0%, #142e6e 100%)' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 40, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔐</div>
          <div style={{ fontWeight: 800, fontSize: 22, color: '#1a3c8f' }}>Set Your Password</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Your account requires a new password before you can continue.</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>New Password</label>
          <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
            placeholder="Min. 8 characters"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Confirm Password</label>
          <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
            placeholder="Repeat password"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        {msg && <div style={{ fontSize: 13, color: msg.startsWith('✅') ? '#16a34a' : '#dc2626', marginBottom: 14 }}>{msg}</div>}
        <button onClick={submit} disabled={loading}
          style={{ width: '100%', padding: '12px', background: '#1a3c8f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Saving…' : 'Set Password & Continue'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MONTHLY STUDENT FEEDBACK (tutor → student)
═══════════════════════════════════════════════════ */
function MonthlyFeedback() {
  const [enrollments, setEnrollments] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [form, setForm] = useState({ student_id:'', subject:'', month: new Date().toISOString().slice(0,7), progress_summary:'', improvements:'', focus_areas:'', chapters_completed:'' });
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    API.get('/enrollment/').then(r => setEnrollments(r.data || [])).catch(() => {});
    API.get('/tutor-feedback/mine').then(r => setFeedbacks(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const students = [...new Map(enrollments.map(e => [e.student_id, { id: e.student_id, name: e.student_name }])).values()];
  const studentSubjects = enrollments.filter(e => String(e.student_id) === String(form.student_id)).map(e => e.subject);

  const submit = async () => {
    if (!form.student_id || !form.subject || !form.month) { setMsg('Please select student, subject and month'); return; }
    if (!form.progress_summary.trim() && !form.improvements.trim() && !form.focus_areas.trim() && !form.chapters_completed.trim()) {
      setMsg('Please fill at least one feedback field'); return;
    }
    setSaving(true); setMsg('');
    try {
      await API.post('/tutor-feedback/', { ...form, student_id: parseInt(form.student_id) });
      setMsg('✓ Feedback saved');
      setForm(f => ({ ...f, progress_summary:'', improvements:'', focus_areas:'', chapters_completed:'' }));
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding:'28px 32px' }}>
      <h3 className="gradient-text" style={{ textAlign:'center', fontWeight:800, fontSize:20, marginBottom:22 }}>📝 Monthly Student Feedback</h3>
      {msg && <div style={{ padding:'11px 18px', borderRadius:10, fontSize:13, fontWeight:600, marginBottom:16, background: String(msg).startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', border:`1px solid ${String(msg).startsWith('✓') ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)'}`, color: String(msg).startsWith('✓') ? '#16a34a' : '#dc2626', cursor:'pointer' }} onClick={() => setMsg('')}>{msg}</div>}

      <div style={card()}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:16 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:6 }}>Student *</label>
            <select className="form-inp" value={form.student_id} onChange={e => setForm(f => ({ ...f, student_id: e.target.value, subject: '' }))}
              style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, outline:'none', background:'#fff' }}>
              <option value="">— Select Student —</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:6 }}>Subject *</label>
            <select className="form-inp" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} disabled={!form.student_id}
              style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, outline:'none', background:'#fff' }}>
              <option value="">— Select Subject —</option>
              {studentSubjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:6 }}>Month *</label>
            <input type="month" value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))} className="form-inp"
              style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, outline:'none' }} />
          </div>
        </div>
        {[['Monthly Progress Summary', 'progress_summary'], ['Improvements Observed', 'improvements'], ['Areas Needing Focus', 'focus_areas'], ['Chapters Completed', 'chapters_completed']].map(([label, key]) => (
          <div key={key} style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:6 }}>{label}</label>
            <textarea value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} rows={3} maxLength={2000}
              style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'vertical', outline:'none', boxSizing:'border-box' }} />
          </div>
        ))}
        <button onClick={submit} disabled={saving}
          style={{ width:'100%', padding:'12px', borderRadius:10, border:'none', background: saving ? '#94a3b8' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, fontSize:14, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving…' : '💾 Save Feedback'}
        </button>
      </div>

      <div style={{ marginTop:24 }}>
        <h4 style={{ fontWeight:800, fontSize:15, color:'#0f1f45', marginBottom:14 }}>My Past Feedback ({feedbacks.length})</h4>
        {feedbacks.length === 0 ? (
          <div style={card({ textAlign:'center', padding:30, color:'#94a3b8' })}>No feedback submitted yet.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {feedbacks.map(f => (
              <div key={f.id} style={card({ padding:18 })}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:6 }}>
                  <div style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>{f.student_name} · {f.subject}</div>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20, background:'#eff6ff', color:'#2554b8' }}>{f.month}</span>
                </div>
                {f.progress_summary && <div style={{ fontSize:12, color:'#475569', marginBottom:4 }}><b>Progress:</b> {f.progress_summary}</div>}
                {f.improvements && <div style={{ fontSize:12, color:'#475569', marginBottom:4 }}><b>Improvements:</b> {f.improvements}</div>}
                {f.focus_areas && <div style={{ fontSize:12, color:'#475569', marginBottom:4 }}><b>Focus Areas:</b> {f.focus_areas}</div>}
                {f.chapters_completed && <div style={{ fontSize:12, color:'#475569' }}><b>Chapters:</b> {f.chapters_completed}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export default function Tutor() {
  const [active, setActive] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const name = localStorage.getItem('name') || 'Companion';
  const forcePwChange = localStorage.getItem('force_password_change') === '1';
  if (forcePwChange) return <ForcePasswordChange />;
  const [unreadRooms, refreshUnread] = useUnreadChats();
  const unreadCount = Object.values(unreadRooms).filter(Boolean).length;

  // Deep-link from push notifications (?nav=chat etc.) and SW postMessage
  useEffect(() => {
    const applyNav = (target) => {
      if (!target) return;
      setActive(target);
      try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}
    };
    const p = new URLSearchParams(window.location.search);
    applyNav(p.get('nav'));
    const onMsg = (e) => {
      if (e.data?.type === 'notification-click' && e.data?.url) {
        try {
          const u = new URL(e.data.url, window.location.origin);
          applyNav(u.searchParams.get('nav'));
        } catch (_) {}
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, []);

  const titles = {
    dashboard:  { h:'Dashboard',       p:`Welcome back, ${name}` },
    profile:    { h:'My Profile',      p:'Your personal and professional details' },
    enrollment: { h:'Enrollment List', p:'Students assigned to you' },
    attendance: { h:'Attendance',      p:'Class attendance records' },
    schedule:   { h:'Schedules',       p:'Your calendar and upcoming sessions' },
    tests:      { h:'Tests & Grades',  p:'Create tests and manage student grades' },
    leave:      { h:'Leave Request',   p:'Apply for leave and track your requests' },
    payments:   { h:'Payment Details',      p:'Your earnings, student hours, and concerns' },
    homework:   { h:'Homework',      p:'Homework you have assigned — students share completed work in chat' },
    help:       { h:'Help & Support',       p:'Contact us for assistance' },
    chat:       { h:'Chat',            p:'Message your students' },
    classreq:   { h:'Class Requests',   p:'Review student class requests' },
    feedback:   { h:'Student Feedback', p:'View feedback submitted by your students' },
    queries:    { h:'My Queries',       p:'Raise and track queries about your students' },
    materials:  { h:'Study Materials',  p:'Browse and download study materials' },
    vr:         { h:'VR Learning',      p:'Review VR content and request VR sets' },
    stufeedback:{ h:'Monthly Feedback', p:'Submit monthly progress feedback for each student' },
  };

  const views = { dashboard:<Dashboard setActive={setActive}/>, profile:<MyProfile/>, enrollment:<EnrollmentList/>, attendance:<Attendance/>, schedule:<ScheduleView/>, tests:<TestsAndGrades/>, leave:<LeaveRequest/>, payments:<TutorPaymentDetails/>, homework:<HomeworkStatus/>, help:<HelpSupport/>, chat:<TutorChat unreadRooms={unreadRooms} onRoomRead={refreshUnread}/>, classreq:<TutorClassRequests/>, feedback:<TutorFeedbackView/>, queries:<CompanionQueries/>, materials:<UserMaterials role="tutor" />, vr:<VRLearning/>, stufeedback:<MonthlyFeedback/> };

  return (
    <div className="app-shell" style={{ background:'#f0f4fb', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <Sidebar active={active} setActive={setActive} unreadCount={unreadCount} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="main-content" style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', minWidth:0 }}>
        {/* Mobile topbar */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#fff', borderBottom:'1px solid #e8edf8', position:'sticky', top:0, zIndex:5 }} className="mobile-topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <img src="/logo.png" alt="logo" style={{ height:28, objectFit:'contain' }} />
            <span style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>Sapience Edu Connect</span>
          </div>
        </div>
        {/* Top bar */}
        <div className="desktop-topbar" style={{ background:'#fff', borderBottom:'1px solid #e8edf8', padding:'18px 32px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:5, boxShadow:'0 2px 12px rgba(37,84,184,0.06)' }}>
          <div>
            <h2 style={{ fontWeight:800, fontSize:20, color:'#0f1f45', letterSpacing:-0.4 }}>{titles[active].h}</h2>
            <p style={{ fontSize:13, color:'#64748b', marginTop:2, fontWeight:500 }}>{titles[active].p}</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:13, color:'#64748b', fontWeight:600 }}>Welcome, {name}</span>
            <div onClick={() => setActive('profile')} style={{ width:38, height:38, borderRadius:'50%', background:'linear-gradient(135deg,#dbeafe,#bfdbfe)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, border:'2px solid #bfdbfe', cursor:'pointer', fontWeight:800, color:'#2554b8', transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.1)';e.currentTarget.style.boxShadow='0 4px 14px rgba(37,84,184,0.25)'}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='none'}}>
              {name[0]?.toUpperCase()}
            </div>
          </div>
        </div>
        <div key={active} style={{ flex:1 }}>
          {views[active]}
        </div>
      </div>
    </div>
  );
}
